import * as paper from 'paper';
import {Point, Group, Path, Size, Segment, Raster, Tool, PointText, ToolEvent, project} from "paper";
import {MathFunctions, SimpleLine, SimpleSize} from "./includes/mathHelper";
import {DrawData, IDrawDataFuncs, EdgeColorData} from "./drawData";
import {DrawingCanvasFormSide} from "./drawingCanvasFormSide";
import {DrawDisplayData} from "./drawDisplayData";
import {DrawDataLoader} from "./drawDataLoader";
import {EDIT_MODES, ApplicationState} from "./includes/applicationState";
import {DisplayFunctions} from "./includes/displayFunctions";
import {MenuService} from "./includes/menuService";
import {ApiService, IdDescriptionPair} from "./includes/apiService";
import {DataLayer} from "./dataLayer";
import {CountertopDrawFunction} from "./includes/countertopDrawFunction";
import {SinkItem} from "./includes/sinkItem";
import {CookTopSplashItem} from "./includes/cookTopSplashItem";
import {CurveItem} from "./includes/curveItem";
import {CursorService} from "./includes/cursorService";
import {EEToolAdapterContextType} from "../types/EEToolAdapterContextType";
import {PopupLocation} from "../types/PopupLocation";
import {ColorPopupResult} from "../types/ColorPopupResult";
import {ProductImage} from "../types/ProductImage";
import {trigger} from 'devextreme/events';
import {EEToolContextType} from "../types/EEToolContextType";

export class Draw {
    canvas: HTMLCanvasElement;
    adapter: EEToolAdapterContextType;
    context: EEToolContextType;
    drawDataObj: DrawData;
    dataLayerObj: DataLayer;
    measurementVisible = DrawDisplayData.QUOTE_INFORMATION_VISIBILITY_TEMPLATE;
    updateView: any;  //updateView is a drawHelperFunctions variable. Do this for scope.
    undoStack: any = []; // stack used for stroring old drawing data for undo actions
    currentSave: any = []; // used for stored current drawing data
    hasStep2: boolean = false;
    strDrawindData: string | null = null; // used for storing preloaded drawing data from server
    tool: paper.Tool;  //  It is needed for mouse events. It is not needed in paperscript. I had to use javascript for context menu.
    suppressMousedown: boolean = false;
    rightMouseClick = false;
    currentAngleSegment = null;
    changeSegment: any;
    changePath: any;
    changeCurve: any;
    changeCornerDiagonalCurve: any;
    changeFullRadiusCurve: any; // The variables to store segment, path, and curve which is modified currently.
    hasHitResult = false;

    bMovingPath = false;
    suppressUndoMenu = false;
    suppressCanvas = false;

    menuInfo: any[] | null | undefined = null;
    currentPoint: any;
    pointtextfocus = false;
    isOutTab = false;

    highlighedPath: paper.Path;

    countertopDrawFunction: CountertopDrawFunction;

    cursorService: CursorService;
    isTempPage = true;
    isEstimate = false;

    constructor(canva: HTMLCanvasElement, adapter: EEToolAdapterContextType, context: EEToolContextType) {
        this.canvas = canva;
        var w: any = window;
        if (!w.paper) {
            w.paper = {};
        }
        ////////////////////////////////////////////////////////////////////////////////
        // Interface
        paper.install(w.paper);
        ///////////////////////////////////////////////////////////
        // Setup paperscript on canvas
        paper.setup(this.canvas);
        this.highlighedPath = new Path();
        this.highlighedPath.strokeColor = new paper.Color('#5972E5');
        this.highlighedPath.strokeWidth = 3;
        this.highlighedPath.remove();
        this.tool = new Tool();
        this.tool.onMouseDown = (e: any) => this.MouseDownForTool(e);
        this.tool.onMouseMove = (e: any) => this.MouseMoveForTool(e);
        this.tool.onMouseDrag = (e: any) => this.MouseDragForTool(e);
        this.tool.onMouseUp = (e: any) => this.MouseUpForTool(e);
        this.countertopDrawFunction = new CountertopDrawFunction(this.tool);
        let drawDataFuncs: IDrawDataFuncs = {
            createAngleDisplay: (angleSegment, segPair) => this.createAngle(angleSegment, segPair),
            createCrossMeasurementGroup: (startCurve) => this.createCrossMeasurementGroup(startCurve),
            createMeasurementGroup: (drawData, measureLineText) => this.drawMeasurementLinesWithArrowsAndText(drawData, measureLineText),
            createEdgeGroup: (edgeData) => this.createEdgeGroup(edgeData),
            createEdgeLaminateGroup: (edgeData) => this.createEdgeLaminateGroup(edgeData),
            createBumpGroup: (bumpGroupData) => this.createBumpGroup(bumpGroupData),
            createSplashGroup: (splashData) => this.createSplashGroup(splashData),
            createCounterPath: (segments) => this.countertopDrawFunction.makePathFromSegments(segments),
            getMeasurementVisibility: () => this.measurementVisible,
            createSink: (sinkTypeData, currentPoint, angle, currentSize, path) => this.addSinkToProject(sinkTypeData, currentPoint, angle, currentSize, path),
            createCookTopSplash: (sinkTypeData, currentPoint, angle, currentSize, path, segment, isNew) => this.addCookTopSplashToProject(sinkTypeData, currentPoint, angle, currentSize, path, segment, isNew),
            createSmoothingSegment: (segment, depth, dlgType, productId, forConvert: boolean = false) => this.radiusOrDiagonal(segment, depth, dlgType, productId, forConvert),
            createRadius: (curve, depth, radDirection) => this.radiusLine(curve, depth, radDirection)
        };

        this.drawDataObj = new DrawData(this.canvas, drawDataFuncs, adapter);
        this.dataLayerObj = new DataLayer(this.drawDataObj);
        this.cursorService = new CursorService(canva);
        this.adapter = adapter;
        this.context = context;
    }

    createAngle(angleSegment: any, segPair: any) {
        var drawDataObj = {pairedSegment: segPair, group: null};
        var ptData = DrawData.calculateAngle(angleSegment);
        (<any>drawDataObj).group = DrawDisplayData.createDisplayedAngle(this.canvas, this.adapter, ptData, true, () => {
            this.currentAngleSegment = angleSegment;
            this.suppressUndoMenu = false;
            this.suppressMousedown = true;
            return DrawDisplayData.extractMeasure(angleSegment.angleDrawDataObj.group.content);
        }, () => (this.measurementVisible.allowAlteringAngles));
        return drawDataObj;
    }

    createCrossMeasurementGroup(startCurve: any) {
        var startSide = DrawData.makeSimpleCounterLine(startCurve);
        if (Math.round(MathFunctions.getDistanceBetween(startSide.point1, startSide.point2)) <= 0) {
            return new Group();
        }

        var dataObj = this.drawDataObj.createCrossMeasurementGroupData(startCurve, 0, true);
        dataObj.startSeg = null;
        dataObj.startSide = startCurve;
        var group = this.drawMeasurementLinesWithArrowsAndText(dataObj, null);
        return group;
    }

    remove() {
        this.highlighedPath.remove();
        this.tool.remove();
        paper.project.remove();
        // @ts-ignore
        this.canvas = undefined;
        var w: any = window;
        if (!w.paper) {
            w.paper = undefined;
        }
        // @ts-ignore
        this.countertopDrawFunction = undefined;
        // @ts-ignore
        this.drawDataObj = undefined;
        // @ts-ignore
        this.dataLayerObj = undefined;
        // @ts-ignore
        this.cursorService = undefined;
    }

    drawMeasurementLinesWithArrowsAndText(drawData: any, measureLineText: any) {
        var start: paper.Point;
        var end: paper.Point;
        var isOuterMeasure;
        var textLocation;

        isOuterMeasure = drawData.isOuterMeasure;

        start = drawData.start;
        end = drawData.end;

        if (drawData.textLocation || drawData.pointTextObj) {
            textLocation = drawData.textLocation ? drawData.textLocation : drawData.pointTextObj.point;
            if (!isOuterMeasure) {
                textLocation = null;
            }
        }

        if (!textLocation) {
            var shift = MathFunctions.getUnitNormal(start, end);
            if (drawData.isRadius) {
                textLocation = MathFunctions.addVectors(MathFunctions.getMidpoint(start, end), shift.normalize(DrawDisplayData.RADIUS_LABEL_SHIFT));
            } else
                textLocation = MathFunctions.addVectors(MathFunctions.getMidpoint(start, end), shift.normalize(DrawData.MEASUREMENT_LABEL_SHIFT));
        }

        var textContent = DisplayFunctions.RoundPixelToInch(MathFunctions.getDistanceBetween(start, end));

        //Update dimension for rectangle shape 
        if (measureLineText != undefined && measureLineText != null) {
            textContent = measureLineText.textContent == undefined ? textContent : measureLineText.textContent;
        }

        var ptData = {
            point: textLocation, content: <any>textContent, drawData: drawData
        };
        var pointText;
        if (drawData.isRadius) {
            if (drawData.isCorner) {
                ptData.content = 'r' + ptData.drawData.radiusData.depth;
            } else {
                ptData.content = 'r' + ptData.content;
            }
            pointText = this.createRadiusMeasurePointText(ptData, drawData.isCorner);
        } else if (isOuterMeasure) {
            pointText = this.createOuterMeasurePointText(ptData);
        } else {
            pointText = this.createInnerMeasurePointText(ptData);
        }

        var endVector = MathFunctions.getVectorBetweenPoints(start, end);
        var startVector = MathFunctions.reverseVector(endVector);
        var startArrow = startVector.normalize(DrawDisplayData.MEASUREMENT_ARROWSIZE);
        var endArrow = endVector.normalize(DrawDisplayData.MEASUREMENT_ARROWSIZE);
        var startEndPath = new Path([start, end]);
        drawData.start = startEndPath.segments[0].point;
        drawData.end = startEndPath.segments[1].point;
        drawData.textLocation = null;
        drawData.pointTextObj = pointText;
        pointText.name = 'label';
        var pathGroup = new Group([
            new Path([
                MathFunctions.addVectors(start, startArrow.rotate(135, <any>null)),
                start,
                MathFunctions.addVectors(start, startArrow.rotate(-135, <any>null))]
            ),
            startEndPath,
            new Path([
                MathFunctions.addVectors(end, endArrow.rotate(135, <any>null)),
                end,
                MathFunctions.addVectors(end, endArrow.rotate(-135, <any>null))]
            )
        ]);

        pathGroup.name = 'path';
        pathGroup.strokeWidth = 0.3;
        pathGroup.strokeColor = new paper.Color('gray');
        var measurementItem = new Group([pathGroup, pointText]);

        return measurementItem;
    }

    createEdgeGroup(edgeData: any) {
        var edgeDrawData = this.createEdgeDrawData(edgeData);
        var group = this.createComplexCurveGroupPath(edgeDrawData);
        return group;
    }

    createEdgeLaminateGroup(edgeData: any) {
        var edgeDrawData = this.createEdgeDrawData(edgeData);
        if (edgeData.segment.data.edgeLaminateProducts) {
            if (edgeData.segment.data.edgeLaminateProducts.capProductId) {
                edgeDrawData.pathStyle = DrawDisplayData.EDGE_LAMINATE_CAP_STYLE;
            }
            if (edgeData.segment.data.edgeLaminateProducts.splashProductId) {
                edgeDrawData.pathStyle = DrawDisplayData.EDGE_LAMINATE_SPLASH_STYLE;
            }
            if (edgeData.segment.data.edgeLaminateProducts.splashProductId && edgeData.segment.data.edgeLaminateProducts.capProductId) {
                edgeDrawData.pathStyle = DrawDisplayData.EDGE_LAMINATE_CAP_SPLASH_STYLE;
            }
        }
        var group = this.createComplexCurveGroupPath(edgeDrawData);
        return group;
    }

    createEdgeDrawData(edgeData: any) {
        var drawDataObj = CurveItem.createCurveLineDrawData({segment: edgeData.segment});
        drawDataObj.pathShift = DrawDisplayData.EDGE_BAR_SHIFT;
        drawDataObj.pathStyle = DrawDisplayData.EDGE_STYLE;
        drawDataObj.skipLabelText = true;

        var that = this;
        drawDataObj.pathClickOptions = {
            mouseOverAllowBoolFunc() {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.EDGE_COLOR);
            },
            doDisplayMenuBoolFunc(group) {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.EDGE_COLOR && !edgeData.segment.data.splashData);
            },
            onClickMenuMakerFunc(group) {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.EDGE_COLOR) ? that.createEdgeMenu(edgeData.segment.path, edgeData.segment.path.curves[edgeData.segment.index]) : null;
            }
        };

        return drawDataObj;
    }

    createComplexCurveGroupPath(dataObj: any) {
        var retVal = new Group();
        var path = CurveItem.createComplexCurvePath(dataObj);
        this.makeClickableGroup(path, dataObj.pathClickOptions);//field
        retVal.addChild(path);
        if (!dataObj.skipLabelText) {    //field
            var labelPointStart = path.position;
            var labelShiftVector;
            if (dataObj.labelPosition) {
                if (dataObj.labelPosition === 'lateral') {
                    var dirToStart = MathFunctions.getVectorBetweenPoints(path.position, path.segments[0].point);
                    labelShiftVector = MathFunctions.addVectors(dirToStart.normalize(2), dataObj.labelShiftVector.normalize(1));
                    labelPointStart = path.segments[0].point;
                    //labelPointStart.y = labelPointStart.y - 4;
                } else {
                    labelShiftVector = dataObj.labelShiftVector;
                }
            }
            labelShiftVector = labelShiftVector.normalize(dataObj.labelShiftLength ? dataObj.labelShiftLength : DrawData.MEASUREMENT_LABEL_SHIFT);//field
            var style = dataObj.labelStyle ? {...dataObj.labelStyle} : {...DrawDisplayData.MEASUREMENT_LABEL_STYLE};//field
            style.point = MathFunctions.addVectors(labelPointStart, labelShiftVector);
            style.content = dataObj.labelText ? dataObj.labelText : '' + DisplayFunctions.RoundPixelToInch(dataObj.length) + 'in.';//field

            if (dataObj.labelText.indexOf("in. splash") > -1) {
                var yConst = 0;
                var xConst = 0;
                var yMax = 0;
                var yMin = 0;
                var xMax = 0;
                var xMin = 0;

                xMax = path.segments[0].point.x;
                xMin = path.segments[0].point.x;

                yMax = path.segments[0].point.y;
                yMin = path.segments[0].point.y;
                for (var i = 0; i < path.segments.length; i++) {
                    //check xMax
                    if (Math.abs(xMax) < Math.abs(path.segments[i].point.x)) {
                        xMax = Math.abs(path.segments[i].point.x);
                    }

                    //Check xMin
                    if (Math.abs(xMin) > Math.abs(path.segments[i].point.x)) {
                        xMin = Math.abs(path.segments[i].point.x);
                    }

                    //check yMax
                    if (Math.abs(yMax) < Math.abs(path.segments[i].point.y)) {
                        yMax = Math.abs(path.segments[i].point.y);
                    }

                    //check yMin
                    if (Math.abs(yMin) > Math.abs(path.segments[i].point.y)) {
                        yMin = Math.abs(path.segments[i].point.y);
                    }
                }

                xConst = (xMax - xMin);
                yConst = (yMax - yMin);

                var x1 = path.segments[1].point.x;
                var x0 = path.segments[3].point.x;
                var y1 = path.segments[1].point.y;
                var y0 = path.segments[3].point.y;

                var xs = (x1 + x0) / 2;
                var ys = (y1 + y0) / 2;

                style.point.x = xs;
                style.point.y = ys;

                if (xConst > yConst) {
                    //style.point.y = style.point.y + 20;
                } else if (yConst > xConst) {
                    style.point.y = style.point.y - 40;
                }
            }
            var pt = new PointText(style);
            this.makeClickablePointText(pt, dataObj.labelClickOptions);//field
            pt.name = 'label';
            retVal.addChild(pt);
        }

        return retVal;
    }

    createBumpGroup(bumpGroupData: any) {
        var tempTangentSide = DrawData.makeSimpleCounterLine(bumpGroupData.tangentSide);
        var tempSide1 = DrawData.makeSimpleCounterLine(bumpGroupData.side1);
        var tempSide2 = DrawData.makeSimpleCounterLine(bumpGroupData.side2);
        var centerPoint = MathFunctions.getMidpoint(MathFunctions.getLineMidpoint(tempSide1), MathFunctions.getLineMidpoint(tempSide2));
        //var centerShift = getVectorBetween(bumpGroupData.side1.point, bumpGroupData.side1.point2).normalize(INVISIBLE_HANDLE_ADDED_SIZE / 2);

        var width = Math.max(DisplayFunctions.PIXEL_INCH_RATE, MathFunctions.getLineLength(tempTangentSide) - DrawDisplayData.INVISIBLE_HANDLE_REDUCE_WIDTH);
        var depth = Math.max(DisplayFunctions.PIXEL_INCH_RATE, MathFunctions.getLineLength(tempSide1) - DrawDisplayData.INVISIBLE_HANDLE_REDUCE_HEIGHT);
        var size = new Size(width, depth);

        //shapeRectangle.bringToFront();
        if (bumpGroupData.bumpGroup) {
            // Not sure what is the idea of this code commented to fix issue with moving bump group
            //bumpGroupData.bumpGroup.removeChildren();
            //bumpGroupData.bumpGroup.addChild(shapeRectangle);
        } else {
            var shapeRectangle = new Path.Rectangle(new Point(0, 0),
                size);

            shapeRectangle.style = <any>DrawDisplayData.INVISIBLE_HANDLE_STYLE;

            var angle = MathFunctions.getVectorBetween(tempTangentSide.point2, tempTangentSide.point1).angle;
            shapeRectangle.rotate(angle);
            shapeRectangle.position = centerPoint;
            shapeRectangle.opacity = DrawDisplayData.INVISIBLE_HANDLE_OPACITY;

            bumpGroupData.bumpGroup = new Group([shapeRectangle]);
            this.setBumpGroupMoveFunction(bumpGroupData);
            DrawData.bringBumpHandleToFront(bumpGroupData.side1);
        }
        return bumpGroupData.bumpGroup;
    }

    createSplashGroup(splashData: any) {
        var drawDataObj = this.createSplashDrawData(splashData);
        var group = this.createComplexCurveGroupPath(drawDataObj);
        if (splashData.selectedUpgrades && splashData.selectedUpgrades.length >= 1) {
            (<any>group).children['label'].content += '*';
        }
        return group;
    }

    createSplashDrawData(splashData: any) {
        var drawDataObj = CurveItem.createCurveLineDrawData({
            segment: splashData.segment,
            curveLineLengthInInches: splashData.splashLengthInInches,
            curveLineOffsetInInches: splashData.splashOffsetInInches
        });
        drawDataObj.thickness = DisplayFunctions.RoundInchToPixel(splashData.splashHeightInInches);
        drawDataObj.pathShift = DrawDisplayData.SPLASH_BAR_SHIFT;
        drawDataObj.pathStyle = DrawDisplayData.SPLASH_STYLE;
        drawDataObj.ignoreCornerRadius = false;
        drawDataObj.labelText = '' + splashData.splashHeightInInches + ' in. splash';
        drawDataObj.labelStyle = DrawDisplayData.SPLASH_LABEL_STYLE;
        drawDataObj.labelPosition = 'lateral';
        var that = this;
        drawDataObj.pathClickOptions = {
            mouseOverAllowBoolFunc() {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH);
            },
            doDisplayMenuBoolFunc(group) {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH && !splashData.segment.data.edgeData);
            },
            onClickMenuMakerFunc(group) {
                return (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH) ? that.createMenuForBacksplash(splashData) : null;
            }
        };
        drawDataObj.labelClickOptions = {
            onEnterFunc(value) {
                splashData.splashHeightInInches = value;
                that.splash(splashData);
            }
        };
        return drawDataObj;
    }

    addSinkToProject(sinkTypeData: any, currentPoint: any, angle: any, currentSize: any, path: any) {
        var sinkData = {
            typeID: sinkTypeData,
            angle: angle ? angle : 0,
            isResizable: false,
            path: path,
            group: <paper.Group | null>null
        };
        //sinkData.group = {data:sinkData, remove: function(){}, rotate: function(a){}, position:currentPoint, bounds:currentSize}; //dummy group.        

        if (!this.drawDataObj.CounterTopArr[path.pathIndex].sinkArr) {
            this.drawDataObj.CounterTopArr[path.pathIndex].sinkArr = [];
        }
        this.drawDataObj.CounterTopArr[path.pathIndex].sinkArr.push(sinkData);
        var addSinkGroupFunc = (raster: any) => {
            sinkData.group = raster;
            raster.children['SinkImage'].group = raster;
            raster.data = sinkData;
            (<any>this.drawDataObj).CounterTopArr[sinkData.path.pathIndex].pathGroup.addChild(sinkData.group);
            (<any>sinkData).group.children['RasterTracker'].sendToBack();
        };
        this.drawDataObj.drawDisplayDataObj.makeSinkGroup(sinkTypeData, currentPoint, angle, currentSize, addSinkGroupFunc);
    }

    addCookTopSplashToProject(sinkTypeData: any, currentPoint: any, angle: any, currentSize: any, path: any, segment: any, isNew: any) {

        var cookTopSplashData = {
            typeID: sinkTypeData,
            angle: angle ? angle : 0,
            isResizable: false,
            path: path,
            segment: null,
            size: null,
            group: <paper.Group | null>null
        };

        if (segment) {
            cookTopSplashData.segment = segment;
        }

        if (currentSize) {
            cookTopSplashData.size = currentSize;
        }

        if (!this.drawDataObj.CounterTopArr[path.pathIndex].cookTopSplashArr) {
            this.drawDataObj.CounterTopArr[path.pathIndex].cookTopSplashArr = [];
        }
        this.drawDataObj.CounterTopArr[path.pathIndex].cookTopSplashArr.push(cookTopSplashData);
        var addCookTopSplashGroupFunc = (raster: any) => {
            cookTopSplashData.group = raster;
            raster.children['CookTopSplashImage'].group = raster;
            raster.data = cookTopSplashData;
            this.drawDataObj.CounterTopArr[cookTopSplashData.path.pathIndex].pathGroup.addChild(<any>cookTopSplashData.group);
            (<any>cookTopSplashData).group.children['CookTopSplashTracker'].sendToBack();
        };

        this.addCookTopSplash(currentPoint, angle, currentSize, addCookTopSplashGroupFunc, segment);
    }

    radiusOrDiagonal(segment: any, depth: any, dlgType: any, selectedOption: any = undefined, forConvert: boolean = false) {
        if ((depth <= 0) && segment.data && (segment.data.smoothingData || segment.data.isSmoothingOnly)) {
            this.drawDataObj.removeDiagonalFromSegment(segment);
        } else {
            var smoothingData: any = {depth: depth, type: dlgType};
            if (selectedOption) {
                smoothingData.productId = selectedOption;
            }

            if (dlgType == 1) {
                return this.smoothSegment(segment, DisplayFunctions.RoundInchToPixel(depth), smoothingData, forConvert);
            } else {

                if (forConvert) {
                    segment = this.diagonalSegment(segment, DisplayFunctions.RoundInchToPixel(depth), smoothingData, false, true);
                    return this.diagonalSegment(segment, DisplayFunctions.RoundInchToPixel(depth), smoothingData, true, true);
                }
                return this.diagonalSegment(segment, DisplayFunctions.RoundInchToPixel(depth), smoothingData, true);
            }
        }
    }

    diagonalSegment(segment: any, dLength: any, smoothingData: any, allowReplaceData: any, dontAddNewEdges: boolean = false) {    //  Function to make a diagonal from a segment.
        // If the segment to modify belongs to diagonal or smoothing curve, then find the right one to modify
        segment = DrawData.getSmoothedEnd(segment);
        var segmentPoint = DrawData.getSmoothedPoint(segment);
        var segStartData = DrawData.getSmoothedStart(segment).data;
        var prevSeg = DrawData.getSmoothedPreviousSegment(segment);
        var nextSeg = DrawData.getSmoothedNextSegment(segment);

        var hasEdge = segStartData.edgeData;
        if (!hasEdge && !dontAddNewEdges) {
            hasEdge = prevSeg.data.edgeData;
        }

        var nextSegPoint = DrawData.getSmoothedPoint(nextSeg);
        var prevSegPoint = DrawData.getSmoothedPoint(prevSeg);
        //Cut out delta length from along both sides.
        var nextSegDepthCheckPoint = segment.next.point; //don't pass this.
        var prevSegDepthCheckPoint = DrawData.getSmoothedStart(segment).previous.point;
        var prevDepth = dLength;//PIXELS;
        var nextDepth = dLength;//in pixels;
        var replaceDepth = false;
        var newDepth;// = FloorPixelToInch(Math.max(prevDepth, nextDepth));
        newDepth = DisplayFunctions.FloorPixelToInch(Math.max((DisplayFunctions.PIXEL_INCH_RATE / 2.0), MathFunctions.getDistanceBetween(segmentPoint, prevSegDepthCheckPoint) - (DisplayFunctions.PIXEL_INCH_RATE / 2.0)));
        if (prevDepth > DisplayFunctions.ConvertExactInchToPixel(newDepth)) {// too deep
            prevDepth = DisplayFunctions.ConvertExactInchToPixel(newDepth);
            replaceDepth = true;
        }
        newDepth = DisplayFunctions.FloorPixelToInch(Math.max((DisplayFunctions.PIXEL_INCH_RATE / 2.0), MathFunctions.getDistanceBetween(segmentPoint, nextSegDepthCheckPoint) - (DisplayFunctions.PIXEL_INCH_RATE / 2.0)));
        if (nextDepth > DisplayFunctions.ConvertExactInchToPixel(newDepth)) {// too deep
            nextDepth = DisplayFunctions.ConvertExactInchToPixel(newDepth);
            replaceDepth = true;
        }
        if (replaceDepth) {
            prevDepth = nextDepth = Math.min(prevDepth, nextDepth);
            if (allowReplaceData) {
                smoothingData.depth = DisplayFunctions.RoundPixelToInch(nextDepth);
            }
        }

        var prevVectorDelta = MathFunctions.reverseVector(MathFunctions.getVectorBetween(prevSegPoint, segmentPoint)).normalize(prevDepth);
        var nextVectorDelta = MathFunctions.getVectorBetween(segmentPoint, nextSegPoint).normalize(nextDepth);
        var pSegPoint = MathFunctions.addVectors(segmentPoint, prevVectorDelta);
        var thisSegPoint = MathFunctions.addVectors(segmentPoint, nextVectorDelta);
        if (!segment.data.isSmoothingOnly)
            segment.path.insert(segment.index, pSegPoint);
        else {
            segment.previous.point = pSegPoint;
            hasEdge = false;
        }
        segment.point = thisSegPoint;
        //segment.handleIn = new Point(0, 0);
        //segment.handleOut = new Point(0, 0);
        prevSeg = segment.previous;
        //prevSeg.handleIn = new Point(0, 0);
        //prevSeg.handleOut = new Point(0, 0);

        if (!prevSeg.data) {
            var tempRadiusData = null;
            var tempCrossSectionGroup = null;
            if (segment.data.radiusData) {
                tempRadiusData = segment.data.radiusData;
                segment.data.radiusData = undefined;
            }
            if (segment.data.crossSectionGroup) {
                tempCrossSectionGroup = segment.data.crossSectionGroup;
                segment.data.crossSectionGroup = undefined;
            }
            prevSeg.data = segment.data;
            segment.data = {};
            if (tempRadiusData) {
                segment.data.radiusData = tempRadiusData;
            }
            if (tempCrossSectionGroup) {
                segment.data.crossSectionGroup = tempCrossSectionGroup;
            }
        } else {
            prevSeg.data.smoothingData.smoothingPointGroup.remove();
            if (prevSeg.data.smoothingData.group)
                prevSeg.data.smoothingData.group.remove();
        }
        prevSeg.isOuterAngle = segment.isOuterAngle;
        var smoothingPointGroup = new Path();
        smoothingPointGroup.add(segmentPoint);
        smoothingPointGroup.position = segmentPoint;
        smoothingPointGroup.strokeColor = new paper.Color('white');
        smoothingPointGroup.visible = false;
        smoothingData.smoothingPointGroup = smoothingPointGroup;
        this.drawDataObj.addToCounterGroup(segment.path, smoothingPointGroup);

        prevSeg.data.smoothingData = smoothingData;

        segment.data.isSmoothingOnly = true;

        if (segStartData.splashData) {
            DrawData.moveCurveData(prevSeg, segment);
        }
        if (hasEdge) {
            var newEdge = {...hasEdge};
            newEdge.segment = prevSeg;
            newEdge.group = null;
            this.edge(newEdge);
            this.edge(hasEdge); //fix weird bug.
        }
        var segStart = DrawData.getSmoothedStart(prevSeg);
        if (segStart.data.splashData) {
            segStart.data.splashData = null;
        }
        return prevSeg;
    }

    radiusLine(curve: any, depth: number, radDirection: number) {
        var radiusData = {
            depth: depth,
            direction: radDirection,
            trueRadius: MathFunctions
                .calculateRadiusFromArc(DisplayFunctions
                        .RoundPixelToInch(MathFunctions.getLineLength(DrawData.makeSimpleCounterLine(curve.segment1))),
                    depth),
            group: null
        };
        DrawData.removeRadius(curve);
        //this.drawDataObj.removeDiagonalFromSegment(curve.segment1); //they don't play nice together.
        //this.drawDataObj.removeDiagonalFromSegment(curve.segment2);
        this.drawDataObj.addRadiusData(curve, radiusData);
        depth = DisplayFunctions.RoundInchToPixel(depth);
        depth = depth * radDirection;
        /////////////////////// Making the curve to smooth curve ///////////////////////////

        var from = new Point(curve.point1);

        var midP = MathFunctions.getMidpoint(curve.point1, curve.point2);
        var depthVector = curve.getNormalAt(0).normalize(depth);
        var through = MathFunctions.addVectors(midP, depthVector);

        var to = new Point(curve.point2);

        var arc = new Path.Arc(from, through, to);
        arc.remove();
        var segment = curve.segment1;
        var tempHandle = new Point(segment.handleIn);
        this.drawDataObj.copyGeometryOfSegment(segment, arc.segments[0]);
        segment.handleIn = tempHandle;
        curve = curve.next;
        segment = curve.segment1;
        tempHandle = new Point(segment.handleOut);
        this.drawDataObj.copyGeometryOfSegment(segment, arc.segments[arc.segments.length - 1]);
        segment.handleOut = tempHandle;
        for (var i = 1; i < arc.segments.length - 1; i++) {
            segment.path.insert(segment.index,
                new Segment({
                    point: arc.segments[i].point,
                    handleIn: arc.segments[i].handleIn,
                    handleOut: arc.segments[i].handleOut
                }));
            segment.previous.data = {};
            DrawData.setIgnoredCurve(curve.previous);
        }

        var firstCurve = curve.previous;
        while (DrawData.isIgnoredCurve(firstCurve)) {
            firstCurve = firstCurve.previous;
        }

        var drawData = {
            start: midP,
            end: through,
            isRadius: true,
            isCurve: true,
            curve: firstCurve,
            radiusData: radiusData
        }
        radiusData.group = <any>this.drawMeasurementLinesWithArrowsAndText(drawData, null);
        this.drawDataObj.addToCounterGroup(curve.path, radiusData.group);

        ////////////////////////////////////////////////////////////////////////////////////
    }

    createMenuCounter(pathGroup: any) {
        var menuCounter = MenuService.makeMenuCounter([
            () => {
                this.drawDataObj.rotatePath(pathGroup, -22.5);
            },
            () => {
                this.drawDataObj.rotatePath(pathGroup, +22.5);
            },
            () => {
                this.drawDataObj.duplicatePath(pathGroup);
            },
            () => {
                this.drawDataObj.removePath(pathGroup);
            }]);
        return menuCounter;
    }

    createMenuLineRadius(curve: any) {
        var menuLineRadius = MenuService.makeMenuLineRadius([
            DrawingCanvasFormSide.dc_makeRadiusLineFunction(
                () => {
                    var radiusData = this.getMatchedRadiusData(curve);
                    if (radiusData && !isNaN(radiusData.depth)) {
                        this.radiusLine(curve, radiusData.depth, radiusData.direction);
                        this.drawDataObj.fullRedraw();
                        return null;
                    }

                    this.hasStep2 = true;
                    return {
                        modalLocation: this.getModalLocation(),
                        inches: DrawDisplayData.DEFAULT_LINE_RADIUS_DEPTH_INCHES
                    };
                },
                (radDepth: number, radDirection: number) => {
                    var ec = this.drawDataObj.getEdgeColorFromPath(curve.segment1.path);
                    if (ec && DataLayer.isLaminate(ec.ec)) {
                        if (radDepth <= 0 || radDepth > 180) {
                            alert('Depth should be between 0 and 180');
                            return;
                        }
                    }
                    this.radiusLine(curve, radDepth, radDirection);
                    this.updateViewAndAddSavePoint();
                    this.drawDataObj.fullRedraw();
                },
                this.adapter),
            () => {
                this.hasStep2 = true;
                this.setupBumpDlg(-1, curve);
            },
            () => {
                this.hasStep2 = true;
                this.setupBumpDlg(1, curve);
            }
        ]);
        return menuLineRadius;
    }

    createMenuCornerRadius(segment: any) {
        var ec = this.drawDataObj.getEdgeColorFromPath(segment.path);
        var isLaminate = (ec && DataLayer.isLaminate(ec.ec)) ?? false;
        var menuCornerRadius = MenuService.makeMenuCornerRadius([() => {
                //hasStep2 = true; setupSelDepthDlg(segment, 1);
                if (segment.data.smoothingData && segment.data.smoothingData.type == 1) {
                    this.hasStep2 = true;
                    this.setupSelDepthDlg(segment, 1, segment.data.smoothingData.depth, undefined);
                } else {
                    this.radiusOrDiagonal(segment, DrawDisplayData.DEFAULT_CORNER_RADIUS_DEPTH_INCHES, 1);
                    this.drawDataObj.fullRedraw();
                }
            },
                () => {
                    //hasStep2 = true; setupSelDepthDlg(segment, 2);
                    var ec = this.drawDataObj.getEdgeColorFromPath(segment.path);
                    if (segment.data.smoothingData && segment.data.smoothingData.type == 2) {
                        var options = undefined;
                        if (ec && DataLayer.isLaminate(ec.ec)) {
                            options = ApiService.getDiagonalOptionListFromServer(<number>this.drawDataObj.getProductQuoteGroup(segment.path));
                        }
                        this.hasStep2 = true;
                        this.setupSelDepthDlg(segment, 2, segment.data.smoothingData.depth, options, segment.data.smoothingData.productId);
                    } else {
                        if (ec && DataLayer.isLaminate(ec.ec)) {
                            this.hasStep2 = true;
                            var options: any = ApiService.getDiagonalOptionListFromServer(<number>this.drawDataObj.getProductQuoteGroup(segment.path));
                            this.setupSelDepthDlg(segment, 2, DrawDisplayData.DEFAULT_CORNER_DIAGONAL_DEPTH_INCHES, options);
                        } else {
                            this.radiusOrDiagonal(segment, DrawDisplayData.DEFAULT_CORNER_DIAGONAL_DEPTH_INCHES, 2);
                            this.drawDataObj.fullRedraw();
                        }

                    }
                },
                () => {
                    var path = segment.path;
                    this.drawDataObj.removeDiagonalFromSegment(segment);
                    this.drawDataObj.redrawInternals(path);
                },
                () => {
                    if (segment.data.smoothingData && segment.data.smoothingData.type == 3) {
                        this.hasStep2 = true;
                        this.setupSelDepthDlg(segment, 3, segment.data.smoothingData.depth, undefined);
                    } else {
                        this.radiusOrDiagonal(segment, DrawDisplayData.DEFAULT_CORNER_DIAGONAL_DEPTH_INCHES, 3);
                        this.drawDataObj.fullRedraw();
                    }
                }],
            (segment.data.smoothingData || segment.data.isSmoothingOnly),
            isLaminate
        );
        return menuCornerRadius;
    }

    createMenuBacksplash(curve: any) {
        var menuBacksplash = MenuService.makeMenuBacksplash([() => {
            this.splash({segment: curve.segment1, splashHeightInInches: 3});
        },
            () => {
                this.splash({segment: curve.segment1, splashHeightInInches: 4});
            },
            DrawingCanvasFormSide.dc_makeSplashFunction(() => {
                this.hasStep2 = true;
                return {modalLocation: this.getModalLocation()};
            }, (height: number) => {
                this.splash({segment: curve.segment1, splashHeightInInches: height});
                this.updateViewAndAddSavePoint();
            }, this.adapter),
            DrawingCanvasFormSide.dc_makeCookTopSplashFunction(() => {
                    this.hasStep2 = true;
                    return {modalLocation: this.getModalLocation()};
                },
                new Size(DrawDisplayData.DEFAULT_OTHER_COOKTOP_SPLASH_WIDTH, DrawDisplayData.DEFAULT_OTHER_COOKTOP_SPLASH_HEIGHT),
                (width: number, height: number) => {
                    this.addCookTopSplashToProject({
                        typeEnum: 10,
                        sinkID: -1
                    }, this.currentPoint, 0, new Size(DisplayFunctions.RoundInchToPixel(width), DisplayFunctions.RoundInchToPixel(height)), curve.segment1.path, curve.segment1, true);
                    this.repositionPath(curve.segment1.path);
                    this.updateViewAndAddSavePoint();
                }, this.adapter) //CookTop Splash
        ]);

        return menuBacksplash;
    }

    createMenuSink(path: any) {
        var menuSink = MenuService.makeMenuSink(
            ApiService.getSinkListFromServer(<any>this.drawDataObj.getProductQuoteGroup(path)),
            () => {
                this.hasStep2 = true;
            },
            sinkId => {
                this.hasStep2 = true;
                this.addSinkToProject({
                    typeEnum: 1,
                    sinkID: sinkId,
                    ironType: -1
                }, this.currentPoint, null, null, path);
                this.updateViewAndAddSavePoint();
            },
            () => {
                this.addSinkToProject({typeEnum: 3, sinkID: -1}, this.currentPoint, null, null, path);
            },
            ///index = 4 for Cast Iron
            sinkId => {
                this.hasStep2 = true;
                this.addSinkToProject({
                    typeEnum: 1,
                    sinkID: sinkId,
                    ironType: 0
                }, this.currentPoint, null, null, path);
                this.updateViewAndAddSavePoint();
            },
            /// index = 5 for Non-Cast Iron
            sinkId => {
                this.hasStep2 = true;
                this.addSinkToProject({
                    typeEnum: 1,
                    sinkID: sinkId,
                    ironType: 1
                }, this.currentPoint, null, null, path);
                this.updateViewAndAddSavePoint();
            },
            () => {
                this.addSinkToProject({typeEnum: 4, sinkID: -1}, this.currentPoint, null, null, path);
            },
            () => {
                this.addSinkToProject({typeEnum: 5, sinkID: -1}, this.currentPoint, null, null, path);
            },
            () => {
                this.addSinkToProject({typeEnum: 6, sinkID: -1}, this.currentPoint, null, null, path);
            },
            sinkId => {
                this.hasStep2 = true;
                this.addSinkToProject({typeEnum: 1, sinkID: sinkId, ironType: 2}, this.currentPoint, null, null, path);
                this.updateViewAndAddSavePoint();
            });
        return menuSink;
    }

    createMenuSinkDetail(sinkRaster: any, isResizable: any) {
        var menuSinkDetail = MenuService.makeMenuSinkDetail(isResizable, [
            () => {
                this.drawDataObj.deleteSink(sinkRaster.data);
            },
            () => {
                this.hasStep2 = true;
                this.rotateSinkFreewheel(sinkRaster);
            },
            () => {
                this.hasStep2 = true;
                this.resizeSinkFreewheel(sinkRaster);
            }]);
        return menuSinkDetail;
    }

    createMenuCookTopSplashDetail(cookTopSplashRaster: any) {
        var menuCookTopSplashDetail = MenuService.makeMenuCookTopSplashDetail([
            () => {
                this.drawDataObj.deleteCookTopSplash(cookTopSplashRaster.data);
            },
            () => {
                this.hasStep2 = true;
                this.rotateSinkFreewheel(cookTopSplashRaster);
            },
            DrawingCanvasFormSide.dc_makeCookTopSplashFunction(
                () => {
                    this.hasStep2 = true;
                    return {modalLocation: this.getModalLocation()};
                },
                new Size(DisplayFunctions.RoundPixelToInch(cookTopSplashRaster.size.width), DisplayFunctions.RoundPixelToInch(cookTopSplashRaster.size.height)),
                (width, height) => {
                    this.drawDataObj.deleteCookTopSplash(cookTopSplashRaster.data);
                    this.addCookTopSplashToProject(
                        {typeEnum: 10, sinkID: -1},
                        this.currentPoint,
                        0,
                        new Size(DisplayFunctions.RoundInchToPixel(width), DisplayFunctions.RoundInchToPixel(height)),
                        cookTopSplashRaster.data.path,
                        cookTopSplashRaster.segment,
                        true);
                    this.repositionPath(cookTopSplashRaster.data.path);
                    this.updateViewAndAddSavePoint();
                }, this.adapter) //CookTop Splash

            //function () { hasStep2 = true; resizeCookTopSplashFreewheel(cookTopSplashRaster); }
        ]);

        return menuCookTopSplashDetail;
    }

    addCookTopSplash(currentPoint: any, currentAngle: any, currentSize: any, addFunc: any, segment: any) {
        var cookTopSplashRaster = new Raster(DrawDataLoader.COOKTOP_SPLASH_IMAGE, currentPoint);
        var path = this.makeRasterCookTopSplashBase(currentPoint, currentSize, currentAngle, cookTopSplashRaster);
        var lableCookTop = this.makePointTextForCookTopSplash(path, 1, currentAngle, currentSize);
        this.positionLoadedRasterCookTopSplash(cookTopSplashRaster, currentSize, currentPoint, currentAngle);
        var cookTopSplash = new Group([path, cookTopSplashRaster, lableCookTop]);
        this.cookTopSplashHackFix(cookTopSplash, segment, currentSize);
        if (addFunc)
            addFunc(cookTopSplash);
        return cookTopSplash;
    }

    cookTopSplashHackFix(cookTopSplash: any, segment: any, currentSize: any) {
        // add two fields to Group, for moving object
        // TODO: fix it
        cookTopSplash.segment = segment;
        cookTopSplash.size = currentSize;
    }

    createMenuRemoveLineRadius(curve: any) {
        var rm = MenuService.makeRemoveRadiusMenu([
            DrawingCanvasFormSide.dc_makeRadiusLineFunction(
                () => {
                    this.hasStep2 = true;
                    return {
                        modalLocation: this.getModalLocation(),
                        inches: curve.segment1.data.radiusData.depth,
                        direction: curve.segment1.data.radiusData.direction
                    };
                },
                (radDepth: number, radDirection: number) => {
                    var ec = this.drawDataObj.getEdgeColorFromPath(curve.segment1.path);
                    if (ec && DataLayer.isLaminate(ec.ec)) {
                        if (radDepth <= 0 || radDepth > 180) {
                            alert('Depth should be between 0 and 180');
                            return;
                        }
                    }
                    this.radiusLine(curve, radDepth, radDirection);
                    this.updateViewAndAddSavePoint();
                }, this.adapter),
            () => {
                this.hasStep2 = false;
                DrawData.removeRadius(curve);
                this.drawDataObj.fullRedraw();
            }
        ]);

        return rm;
    }

    getModalLocation(): PopupLocation {
        return {
            top: this.currentPoint.y + this.canvas.offsetTop - window.scrollY,
            left: this.currentPoint.x + this.canvas.offsetLeft - window.scrollX
        };
    }

    createEdgeMenu(path: any, curve: any) {
        var menu = [];
        var edgeDataObj = path.segments[curve.index].data.edgeData;
        var ec = this.drawDataObj.getEdgeColorFromPath(path);

        if (!ec) {
            return null;
        }

        var edgeLaminateProducts = path.segments[curve.index].data.edgeLaminateProducts;

        if (!edgeLaminateProducts) {
            if (edgeDataObj) {
                menu.push(MenuService.makeEdgeRemoveMenuItem(false, () => {
                    DrawData.removeEdgeSelection(path.segments[curve.index]);
                }));
            } else {
                var edge1Menu = MenuService.makeEdgeMenuItem(<string>ec.ec.e1Text, (ec.ec.e1 == 'none'), () => {
                    this.edge({segment: curve.segment1, edgeID: 1});
                });//LegendText was 'E1'
                menu.push(edge1Menu);
            }
        }

        if (!edgeDataObj && DataLayer.isLaminate(ec.ec)) {
            var edgeLaminateProducts = path.segments[curve.index].data.edgeLaminateProducts;
            var productQuoteGroup = this.drawDataObj.getProductQuoteGroup(path)
            if (edgeLaminateProducts && edgeLaminateProducts.capProductId) {
                menu.push(MenuService.makeCapRemoveMenuItem(false, () => {
                    this.removeLaminate(path.pathIndex, curve.index, true);
                }));
            } else {
                var menuCap = MenuService.makeEndCap(false, () => {
                    this.setupLaminateDlg(productQuoteGroup, path.pathIndex, curve.index, true);
                });
                menu.push(menuCap);
            }

            if (edgeLaminateProducts && edgeLaminateProducts.splashProductId) {
                menu.push(MenuService.makeSplashRemoveMenuItem(false, () => {
                    this.removeLaminate(path.pathIndex, curve.index, false)
                }));
            } else {
                var menuSplash = MenuService.makeEndSplash(false, () => {
                    this.setupLaminateDlg(productQuoteGroup, path.pathIndex, curve.index, false);
                });
                menu.push(menuSplash);
            }
        }

        return menu;
    }

    createMenuForBacksplash(splashData: any) {
        this.checkSplashSelectedUpgradesForValidity(splashData);
        var productTypeID;
        var ec = this.drawDataObj.getEdgeColorFromPath(splashData.segment.path);
        if (ec) {
            productTypeID = ec.ec.p;
        }
        var myMenu;
        if (productTypeID)
            myMenu = this.getBacksplashUpgradeMenu(splashData, productTypeID);
        else
            myMenu = MenuService.getBacksplashNoUpgradesMenu();
        var that = this;
        var splashMenuItems = MenuService.createSplashMenuItems([
            () => {
                this.drawDataObj.removeSplash(splashData);
            },
            () => {
                this.addSinkToProject({
                    typeEnum: 2,
                    sinkIDFunc: () => ApiService.getCutoutProductID(<number>that.drawDataObj.getProductQuoteGroup(splashData.segment.path))
                }, this.currentPoint, null, null, splashData.segment.path);
            },
            DrawingCanvasFormSide.dc_makeCookTopSplashFunction(() => {
                    this.hasStep2 = true;
                    return {modalLocation: this.getModalLocation()};
                },
                new Size(DrawDisplayData.DEFAULT_OTHER_COOKTOP_SPLASH_WIDTH, DrawDisplayData.DEFAULT_OTHER_COOKTOP_SPLASH_HEIGHT),
                (width, height) => {
                    this.addCookTopSplashToProject({
                        typeEnum: 10,
                        sinkID: -1
                    }, this.currentPoint, 0, new Size(DisplayFunctions.RoundInchToPixel(width), DisplayFunctions.RoundInchToPixel(height)), splashData.segment.path, splashData.segment, true);
                    this.repositionPath(splashData.segment.path);
                    this.updateViewAndAddSavePoint();
                }, this.adapter) //CookTop Splash
        ]);
        for (var i = 0; i < splashMenuItems.length; i++) {
            (<any[]>myMenu).push(splashMenuItems[i]);
        }
        splashData.lastProductTypeID = productTypeID;

        return myMenu;
    }

    repositionPath(path: any) {

        var cookTopSplashArr = this.drawDataObj.CounterTopArr[path.pathIndex].cookTopSplashArr;
        var moveX: number, moveY: number, maxX: number = 0, maxY: number = 0;

        for (let i = 0; i < cookTopSplashArr.length; i++) {
            if (cookTopSplashArr[i] && cookTopSplashArr[i].group &&
                cookTopSplashArr[i].group.children['CookTopSplashTracker'] &&
                cookTopSplashArr[i].group.children['CookTopSplashTracker'].segments) {
                var cookTopSplashSegments = cookTopSplashArr[i].group.children['CookTopSplashTracker'].segments;

                for (var j = 0; j < cookTopSplashSegments.length; j++) {
                    if (cookTopSplashSegments[j] && cookTopSplashSegments[j].point) {
                        if ((cookTopSplashSegments[j].point.x - DrawData.MEASUREMENT_LINE_SHIFT) < 0) {
                            moveX = Math.abs(cookTopSplashSegments[j].point.x - DrawData.MEASUREMENT_LINE_SHIFT) + 50;
                            if (moveX > maxX) {
                                maxX = moveX;
                            }
                        }
                        if ((cookTopSplashSegments[j].point.y - DrawData.MEASUREMENT_LINE_SHIFT) < 0) {
                            moveY = Math.abs(cookTopSplashSegments[j].point.y - DrawData.MEASUREMENT_LINE_SHIFT) + 50;
                            if (moveY > maxY) {
                                maxY = moveY;
                            }
                        }
                    }
                }
            }
        }

        for (let i = 0; i < this.drawDataObj.CounterTopArr.length; i++) {
            var groupPath = this.drawDataObj.getGroupFromPathIndex(i);
            var pathPosition = new Point(groupPath.position);

            pathPosition.x += maxX;
            pathPosition.y += maxY;

            groupPath.position = pathPosition;
        }
    }

    getBacksplashUpgradeMenu(data: any, productTypeID: any) {
        var menu = [];
        var removeMenu = [];
        var upgradeMenu = ApiService.getSplashUpgradeList(productTypeID);
        if (!upgradeMenu || upgradeMenu.length === 0) {
            return MenuService.getBacksplashNoUpgradesMenu();
        }
        var j;
        if (data.selectedUpgrades) {

            for (var i = 0; i < data.selectedUpgrades.length; i++) {

                removeMenu.push(i);
                for (j = 0; j < upgradeMenu.length; j++) {
                    if (upgradeMenu[j].Id == data.selectedUpgrades[i].Id) {
                        upgradeMenu.splice(j, 1);
                        j--;
                    }
                }
            }
        }

        for (j = 0; j < removeMenu.length; j++) {
            (upgradeItemIndex => {
                var upgradeItem = data.selectedUpgrades[upgradeItemIndex];
                menu.push(MenuService.createSplashRemoveMenuItem(
                    upgradeItem.Description,
                    () => {
                        this.hasStep2 = true;
                        this.drawDataObj.removeSplashUpgradeSelection(upgradeItemIndex, data);
                        this.updateViewAndAddSavePoint();
                        this.hasStep2 = false;
                    }));
            })(removeMenu[j]);
        }

        for (j = 0; j < upgradeMenu.length; j++) {
            (upgradeItem => {
                menu.push(
                    MenuService.createSplashUpgradeMenuItem(
                        upgradeItem.Description,
                        () => {
                            this.hasStep2 = true;
                            this.drawDataObj.addSplashUpgradeSelection(upgradeItem, data);
                            this.updateViewAndAddSavePoint();
                            this.hasStep2 = false;
                        }));
            })(upgradeMenu[j]);
        }

        return menu;
    }

    getChangeObjects(eventPoint: paper.Point, hitOptions: any) {
        if (!hitOptions) {
            hitOptions = DrawData.HITOPTIONS;
        }
        var myChangeSegment = null;
        var myChangePath = null;
        var myChangeCurve: paper.Curve | null = null;
        var myChangeCornerDiagonalCurve = null;
        var myChangeFullRadiusCurve = null;
        var mySelectedSink = null;
        var mySelectedCookTopSplash = null;
        var myCurrentPoint = null;
        var myHasHitResult = false;
        var hitResult = this.drawDataObj.customHitTest(paper.project, eventPoint, hitOptions); // Hit Test to recognize which object was selected.
        myCurrentPoint = eventPoint;

        if (hitResult) {
            myHasHitResult = true;
            if (hitResult) { // There is any object
                myChangePath = hitResult.item;
                var skip = false;
                if (hitResult.item.className === "PointText") {
                    myChangePath = this.drawDataObj.getPathFromEdgeColorGroup(hitResult.item);
                    skip = true;
                }
                if (hitResult.item.className === "Raster" &&
                    (ApplicationState.CURRENTMODE === EDIT_MODES.SINK_COOKTOP ||
                        (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH &&
                            hitResult.item.group &&
                            hitResult.item.group.data &&
                            hitResult.item.group.data.typeID &&
                            (hitResult.item.group.data.typeID.typeEnum == 2)))) {
                    myChangePath = null;
                    mySelectedSink = hitResult.item.group;
                    skip = true;
                }

                if (hitResult.item.className === "Raster" &&
                    (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH &&
                        hitResult.item.group &&
                        hitResult.item.group.data &&
                        hitResult.item.group.data.typeID &&
                        (hitResult.item.group.data.typeID.typeEnum == 10))) {
                    myChangePath = null;
                    mySelectedCookTopSplash = hitResult.item.group;
                    skip = true;
                }

                var pathIndex = this.drawDataObj.getPathIndex(myChangePath);
                if (pathIndex === -1 && !skip) {
                    var myChangePathObj = this.drawDataObj.getPathFromHitTest(eventPoint, DrawData.HITOPTIONS);
                    if (!myChangePathObj) {
                        myChangePath = null;
                    } else {
                        myChangePath = myChangePathObj.path;
                        hitResult = myChangePathObj.hitResult;
                    }
                }
                if (myChangePath != null) {
                    this.bringToFront(myChangePath);
                    if (hitResult.item.className === 'Raster') {
                        if (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH &&
                            hitResult.item.group &&
                            hitResult.item.group.data &&
                            hitResult.item.group.data.typeID &&
                            (hitResult.item.group.data.typeID.typeEnum == 10)) {
                            mySelectedCookTopSplash = hitResult.item.group;
                        } else {
                            //counter-attatched sink.
                            mySelectedSink = hitResult.item.group;
                        }
                    } else if (hitResult.type === 'segment') { // A segment was selected
                        myChangeSegment = hitResult.segment;
                        var retData = this.getChangeSegmentType(myChangeSegment);
                        //myChangeSegment, myChangeCurve, myChangeFullRadiusCurve;
                        myChangeSegment = retData.myChangeSegment;
                        myChangeCurve = retData.myChangeCurve;
                        myChangeFullRadiusCurve = retData.myChangeFullRadiusCurve;
                    } else if (hitResult.type === 'stroke') { // A curve was selected

                        myChangeCurve = hitResult.location.curve;

                        var retData2 = this.getChangeCurveType(myChangeCurve);
                        //retData. myChangeCurve, myChangeCornerDiagonalCurve, myChangeFullRadiusCurve
                        myChangeCurve = retData2.myChangeCurve;
                        myChangeCornerDiagonalCurve = retData2.myChangeCornerDiagonalCurve;
                        myChangeFullRadiusCurve = retData2.myChangeFullRadiusCurve;

                    } else if (hitResult.type == 'fill') { // A path was selected ( background was selected )

                    }
                } else if (hitResult.item._index > 0) {
                    if (ApplicationState.CURRENTMODE === EDIT_MODES.SINK_COOKTOP && hitResult.item.className === 'Raster') {
                        mySelectedSink = hitResult.item.group;
                        mySelectedSink.bringToFront();
                    }
                    if (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH && hitResult.item.className === 'Raster') {
                        mySelectedCookTopSplash = hitResult.item.group;
                        mySelectedCookTopSplash.bringToFront();
                    }
                }
            }
        }
        return {
            myChangeSegment: myChangeSegment,
            myChangePath: myChangePath,
            myChangeCurve: myChangeCurve,
            myChangeCornerDiagonalCurve: myChangeCornerDiagonalCurve,
            myChangeFullRadiusCurve: myChangeFullRadiusCurve,
            mySelectedSink: mySelectedSink,
            myCurrentPoint: myCurrentPoint,
            myHasHitResult: myHasHitResult,
            hitResult: hitResult,
            mySelectedCookTopSplash: mySelectedCookTopSplash
        };
    }

    VisibleMeasurement(visibilityTemplate?: any) {
        if (!visibilityTemplate)
            visibilityTemplate = this.measurementVisible;
        this.measurementVisible.suppressCursors = this.measurementVisible.suppressCursors || this.measurementVisible.alwaysSuppressCursors;
        this.drawDataObj.setMeasurementVisibility(visibilityTemplate);
        this.measurementVisible = visibilityTemplate;
        paper.view.update();
    }

    suppressCursors() {
        this.measurementVisible.suppressCursors = true;
    }

    allowCursors() {
        this.measurementVisible.suppressCursors = this.measurementVisible.alwaysSuppressCursors;
    }

    suppressAllCursors() {
        this.suppressCursors();
        this.measurementVisible.suppressAltCursors = true;
    }

    allowAllCursors() {
        this.allowCursors();
        this.measurementVisible.suppressAltCursors = this.measurementVisible.alwaysSuppressCursors;
    }

    setBumpGroupMoveFunction(bumpGroupData: any) {
        var didMoveObj = {
            didMove: false,
            moving: false
        };
        var myFunc = this.drawDataObj.makeAllowedBumpMovementFunction(bumpGroupData, didMoveObj);
        var myMenu = MenuService.makeRemoveBumpMenu(() => {
            this.drawDataObj.removeBumpFromPath(bumpGroupData);
        });
        var myMouseUpFunc = (e: any) => {
            //document.removeEventListener("mouseup", myFuncHolder.myMouseUpFunc);
            this.tool.onMouseUp = (ev: any) => this.MouseUpForTool(ev);
            this.allowAllCursors();
            if (didMoveObj.moving) {
                this.drawDataObj.convertCounterToComplexCounter(bumpGroupData.side1.path);
                if (didMoveObj.didMove)
                    this.updateViewAndAddSavePoint();//creates an undo point.
            }
            didMoveObj.didMove = false;
            didMoveObj.moving = false;
        };

        var myFuncHolder = {myMouseUpFunc: myMouseUpFunc};
        bumpGroupData.bumpGroup.onMouseMove = (event: any) => {
            this.highlighedPath.remove();
            if (this.measurementVisible.suppressAltCursors || this.measurementVisible.alwaysSuppressCursors || !this.isTempPage) {
                //dc_changeMouseCursor('default');
            } else if (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES) {
                this.suppressCursors();
                bumpGroupData.bumpGroup.sendToBack();
                var changeObjects = this.getChangeObjects(event.point, DrawData.HITOPTIONS); // Hit Test to recognize which object was selected.
                bumpGroupData.bumpGroup.bringToFront();
                if (changeObjects.hitResult && changeObjects.hitResult.item && changeObjects.hitResult.item.className == "PointText") {
                    this.cursorService.setPointTextCursor(this.measurementVisible.allowAlteringMeasurements, changeObjects.hitResult.item);
                } else {
                    DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'move');
                }
            } else {
                bumpGroupData.bumpGroup.sendToBack();
            }

        };
        bumpGroupData.bumpGroup.onMouseLeave = (e: any) => {
            if (!this.measurementVisible.suppressAltCursors) {
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                this.allowCursors();
            }
        };
        bumpGroupData.bumpGroup.onMouseDrag = (e: any) => {
            if (this.isTempPage) {
                if (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES) {
                    myFunc(e);
                }
            }
        };
        bumpGroupData.bumpGroup.onMouseDown = (e: any) => {
            var returnNow = false;
            //document.addEventListener("mouseup", myFuncHolder.myMouseUpFunc);
            didMoveObj.didMove = false;
            if (this.isTempPage) {
                if (e.event.button != 2) {
                    bumpGroupData.bumpGroup.sendToBack();
                    var hitResult = this.drawDataObj.customHitTest(paper.project, e.point, DrawData.HITOPTIONS); // Hit Test to recognize which object was selected.
                    if (hitResult) {    // There is any object
                        if (hitResult.item.className === "PointText") {
                            bumpGroupData.bumpGroup.bringToFront();
                            hitResult.item.emit('mousedown');
                            returnNow = true;
                        }
                    }
                }
                if (!returnNow) {
                    if (e.event.button == 2) {
                        if (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES) {
                            this.suppressMousedown = true;
                            this.rightMouseClick = true;
                            this.launchContextMenu(myMenu);

                            return false;
                        }
                        bumpGroupData.bumpGroup.sendToBack();
                        return;
                    }
                    didMoveObj.moving = true;
                    DrawData.convertCounterToSimpleCounter(bumpGroupData.side1.path);
                    this.tool.onMouseUp = myFuncHolder.myMouseUpFunc;
                }
            }
        };
    }

    MouseDownForTool(event: paper.ToolEvent) {   // Mouse down function on canvas
        var mouseEvent: MouseEvent = (<any>event).event;
        if (mouseEvent.button === 2) {
            this.rightMouseClick = true;
        } else {
            this.rightMouseClick = false;
        }
        var eventPoint = event.point;
        if (!this.suppressMousedown && !this.suppressCanvas) {
            this.adapter.hideAngle();
            this.measurementVisible.suppressAltCursors = this.measurementVisible.alwaysSuppressCursors;
            this.setChangeObjects(eventPoint);

            switch (ApplicationState.CURRENTMODE) {
                case EDIT_MODES.QUOTE_INFORMATION: {
                    break;
                }
                case EDIT_MODES.DRAW_COUNTERS: {
                    this.MouseDownForDrawCounters();
                    break;
                }
                case EDIT_MODES.EDGE_COLOR: {
                    this.MouseDownForEdgeColor();
                    break;
                }
                case EDIT_MODES.BUMPS_RADIUSES: {
                    if (this.hasEdgeColorSelection()) {
                        this.MouseDownForBump();
                    }
                    break;
                }
                case EDIT_MODES.BACKSPLASH: {
                    if (this.hasEdgeColorSelection()) {
                        this.MouseDownForBacksplash();
                    }
                    break;
                }
                case EDIT_MODES.SINK_COOKTOP: {
                    this.MouseDownForSink();
                    break;
                }
                case EDIT_MODES.OPTIONAL_ITEMS: {
                    break;
                }
                case EDIT_MODES.PRICING_PRINTING: {
                    break;
                }
            }

            if (this.changeCurve) {
                this.setHighlightedCurve(this.changeCurve);
            } else {
                if (!this.rightMouseClick) {
                    this.highlighedPath.remove();
                }
            }

            this.launchContextMenu();
        }
        this.suppressMousedown = false;
    }

    setChangeObjects(eventPoint: paper.Point, hitOptions?: any) {
        this.resetChangeObjects();
        this.allowCursors();
        var changeObjects = this.getChangeObjects(eventPoint, hitOptions);
        //myChangeSegment, myChangePath, myChangeCurve, myChangeCornerDiagonalCurve, myChangeFullRadiusCurve,
        //mySelectedNonCounterItem, mySelectedSink, myCurrentPoint, myHasHitResult
        this.changeSegment = changeObjects.myChangeSegment;
        this.changePath = changeObjects.myChangePath;
        this.changeCurve = changeObjects.myChangeCurve;
        this.changeCornerDiagonalCurve = changeObjects.myChangeCornerDiagonalCurve;
        this.changeFullRadiusCurve = changeObjects.myChangeFullRadiusCurve;
        SinkItem.setSelectedSink(changeObjects.mySelectedSink);
        CookTopSplashItem.setSelectedCookTopSplash(changeObjects.mySelectedCookTopSplash);
        this.currentPoint = changeObjects.myCurrentPoint;
        //selectedCookTopSplash = changeObjects.mySelectedCookTopSplash;
        this.hasHitResult = changeObjects.myHasHitResult;
        if (this.hasHitResult) {    // There is any object
            this.countertopDrawFunction.bDrawing = false;
        }
    }

    launchContextMenu(myMenuInfo?: any) {
        if (myMenuInfo) {
            this.menuInfo = myMenuInfo;
        }
        if (this.rightMouseClick) {
            if (this.menuInfo && this.menuInfo.length > 0) {
                var tempMenu = this.menuInfo;
                this.menuInfo = null;
                this.suppressAllCursors();
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                var undoWasSelected = false;
                this.adapter.showContextMenu(MenuService.addUndoMenuItem(tempMenu, () => {
                    this.undo();
                    undoWasSelected = true;
                }), () => {
                    if (!this.hasStep2) {
                        this.updateViewAndAddSavePoint(!undoWasSelected);
                    }
                    this.allowAllCursors();
                    this.resetMenuTrackers();
                });
            } else if (!this.suppressUndoMenu && !this.hasStep2) {

                this.suppressAllCursors();
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                ;
                this.adapter.showContextMenu(MenuService.addUndoMenuItem([], () => this.undo(), this.undoStack.length === 0),
                    () => {
                        this.allowAllCursors();
                        this.resetMenuTrackers();
                    });
            } else
                this.resetMenuTrackers();
        } else {
            this.resetMenuTrackers();
            this.menuInfo = null;
        }
    }

    updateViewAndAddSavePoint(updateLastSave: boolean = true) {
        //04/04/2017: Liem Bui update for [Sage-654]
        if (this.countertopDrawFunction.isEdgeInvalid) {
            this.countertopDrawFunction.reSetEdgeInvalidObjs(this.drawDataObj);
        }

        if (updateLastSave) {
            this.undoStack.push(this.currentSave);
        }
        this.currentSave = this.drawDataObj.createSaveDrawData();
        this.redrawUsingSaveData(this.currentSave);
        this.updateEstimate();
    }

    resetMenuTrackers() {
        this.hasStep2 = false;
        this.suppressMousedown = false;
        this.suppressUndoMenu = false;
    }

    /** Mouse move function on canvas. It differs with Mouse Drag function */
    MouseMoveForTool(event: paper.ToolEvent) {    //
        if (true) {
            if (ApplicationState.CURRENTMODE !== EDIT_MODES.PRICING_PRINTING) {
                this.doCursorHitTest(event);
            }
        }
    }

    MouseDragForTool(event: paper.ToolEvent) {    // Mouse Drag function on canvas
        if (true) {
            if (this.rightMouseClick)        //  Mouse Right Button Click
            {
                return;
            }
            if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS || (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES && this.changeCurve && this.changeCurve.segment1.data && this.changeCurve.segment1.data.bumpGroupData)) {
                if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS && this.changeCornerDiagonalCurve) {
                    this.changeSegment = this.changeCornerDiagonalCurve.segment2;
                }
                if (this.changeSegment || this.changeCurve) {
                    this.suppressCursors();
                    DrawData.convertCounterToSimpleCounter(this.changePath);
                    if (this.changeSegment) {
                        this.moveSegment(this.changeSegment, event.delta);
                    } // Modify the point of segment.
                    else if (this.changeCurve) {
                        CurveItem.moveCurve(this.drawDataObj, this.changeCurve, event.delta);
                        this.setHighlightedCurve(this.changeCurve);
                    } // Modify the point of curve.
                } else if (this.changePath) {
                    this.suppressCursors();
                    this.bMovingPath = true;
                    this.movePath(this.drawDataObj.getGroupFromPathIndex(this.changePath.pathIndex), event.delta);    // Modify the point of path or group.
                } else if (this.countertopDrawFunction.bDrawing) {
                    this.suppressCursors();
                    this.countertopDrawFunction.drawing(event, false);              // Drawing
                }
            } else if (ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH) {
                if (SinkItem.hasEnumType(2)) {
                    SinkItem.movingSink(event.delta, this.drawDataObj);  // Moving sink
                } else if (CookTopSplashItem.hasEnumType(10)) {
                    CookTopSplashItem.movingCookTopSplash(event.delta);  // Moving sink
                }
            } else if (ApplicationState.CURRENTMODE === EDIT_MODES.SINK_COOKTOP) {
                if (SinkItem.hasNotEnumType(2)) {
                    SinkItem.movingSink(event.delta, this.drawDataObj);  // Moving sink
                }
            }
        }
    }

    moveSegment(segment: any, delta: any, ignoreLimits: boolean = false) {
        var curve2 = segment.path.curves[segment.index],
            curve1 = curve2.previous;

        CurveItem.moveCurve(this.drawDataObj, curve1, delta, ignoreLimits);
        CurveItem.moveCurve(this.drawDataObj, curve2, delta, ignoreLimits);
    }

    MouseUpForTool(event: paper.ToolEvent) {
        if (true) {
            if (this.rightMouseClick) {
                this.rightMouseClick = false;
                return;   // Right Click
            }
            if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS || (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES && this.changeCurve && this.changeCurve.segment1.data && this.changeCurve.segment1.data.bumpGroupData)) {
                if (this.countertopDrawFunction.bDrawing) {    //  Ended Mouse dragging.
                    this.allowCursors();
                    if (this.countertopDrawFunction.endDraw(event)) {// Completed the path, made a CounterTop.
                        this.drawMeasurement(this.countertopDrawFunction.completedPath);
                        this.updateViewAndAddSavePoint();//Adds a savePoint for Undo.
                    } else {
                        paper.view.update();//Doesn't.
                    }
                }
                if (this.changeCurve) {
                    //checkSidesForConsistency(changeCurve.path);
                    this.changeCurveOnMouseUp(this.changeCurve);
                    this.allowCursors();
                    this.updateViewAndAddSavePoint();
                    this.setHighlightedCurve(this.changeCurve);
                }
                if (this.changeSegment) {
                    //checkSidesForConsistency(changeSegment.path);
                    this.changeSegmentOnMouseUp(this.changeSegment);
                    this.allowCursors();
                    this.updateViewAndAddSavePoint();
                }
                if (this.bMovingPath) {
                    this.allowCursors();
                    this.bMovingPath = false;
                    //checkSidesForConsistency(changePath);
                    this.updateViewAndAddSavePoint();
                }
            } else if (ApplicationState.CURRENTMODE === EDIT_MODES.SINK_COOKTOP || ApplicationState.CURRENTMODE === EDIT_MODES.BACKSPLASH) {
                if (SinkItem.selectedSink && SinkItem.selectedSink.data && SinkItem.selectedSink.data.typeID) {
                    if (SinkItem.didMoveSink) {
                        this.updateViewAndAddSavePoint();
                    }
                }
                if (CookTopSplashItem.selectedCookTopSplash && CookTopSplashItem.selectedCookTopSplash.data && CookTopSplashItem.selectedCookTopSplash.data.typeID) {
                    if (CookTopSplashItem.didMoveCookTopSplash) {
                        this.updateViewAndAddSavePoint();
                    }
                }
                SinkItem.setSelectedSink(null);
                CookTopSplashItem.setSelectedCookTopSplash(null);
            }
        }
    }

    launchContextMenuMouseMove(evn: paper.ToolEvent) {
        if (this.isTempPage && evn) {
            if (this.menuInfo && this.menuInfo.length > 0) {
                var tempMenu = this.menuInfo;
                this.menuInfo = null;
                this.suppressAllCursors();
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                var undoWasSelected = false;
                this.adapter.showContextMenu(MenuService.addUndoMenuItem(tempMenu, () => {
                    this.undo();
                    undoWasSelected = true;
                }), () => {
                    if (!this.hasStep2) {
                        this.updateViewAndAddSavePoint(!undoWasSelected);
                    }
                    this.allowAllCursors();
                    this.resetMenuTrackers();
                });
                ApplicationState.contextMenuX = evn.point.x;
                ApplicationState.contextMenuY = evn.point.y;
                trigger(this.canvas, 'customeetoolcontentmenu');
            } else if (!this.suppressUndoMenu && !this.hasStep2) {

                this.suppressAllCursors();
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                this.adapter.showContextMenu(MenuService.addUndoMenuItem([], () => this.undo(), this.undoStack.length === 0), () => {
                    this.allowAllCursors();
                    this.resetMenuTrackers();
                });
                trigger(this.canvas, 'customeetoolcontentmenu');
            } else {
                this.resetMenuTrackers();
            }
        }
    }

    rotateSinkFreewheel(sinkRaster: any) {
        this.suppressCursors();
        DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'all-scroll');
        sinkRaster.selected = true;
        var lastDiffSign = 0;
        var curAngle = 0;
        this.tool.onMouseMove = function (e: any) {
            var axisPoint = sinkRaster.position;
            var mousePoint = e.point;

            var vector = MathFunctions.getVectorBetween(axisPoint, mousePoint);
            var newAngle = vector.angle + 90;

            if (Math.abs(newAngle - curAngle) >= 7.5) {

                var angleDiff = newAngle - curAngle;
                var angleDiffSign;
                if (Math.abs(angleDiff) < 300) {
                    angleDiffSign = (angleDiff > 0) ? 1 : -1;
                    lastDiffSign = angleDiffSign;
                } else {
                    angleDiffSign = lastDiffSign;
                }

                var rotationAngle = 7.5;

                if (angleDiffSign === -1)
                    rotationAngle = -7.5;

                sinkRaster.rotate(rotationAngle);
                DrawData.updateRasterAngle(sinkRaster.data, rotationAngle);

                curAngle += (newAngle - curAngle);
                var sign = (curAngle > 0) ? 1 : -1;
                curAngle = (Math.abs(curAngle) % 360) * sign;
            }
        };
        this.tool.onMouseDrag = null;
        this.tool.onMouseDown = null;
        this.tool.onMouseUp = (e: any) => {
            this.allowCursors();
            sinkRaster.selected = false;
            this.tool.onMouseDown = (e: paper.ToolEvent) => this.MouseDownForTool(e);
            this.tool.onMouseMove = (e: any) => this.MouseMoveForTool(e);
            this.tool.onMouseDrag = (e: any) => this.MouseDragForTool(e);
            this.tool.onMouseUp = (e: any) => this.MouseUpForTool(e);
            DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
            this.MouseUpForTool(e);
            this.updateViewAndAddSavePoint();
        };
    }

    MouseDownForDrawCounters() {
        if (this.hasHitResult) {
            if (this.changePath && !this.changeSegment && !this.changeCurve) {
                // A path was selected
                this.menuInfo = this.createMenuCounter(DrawData.getGroupFromPath(this.changePath));
            }
        } else {  // No selected objects. So start to draw.   Initialize variables in mouse down.
            if (!this.rightMouseClick) {
                var tempActualStartPoint = this.currentPoint;
                var tempStartPoint = this.transformWithBuffer(this.currentPoint);
                this.countertopDrawFunction.startDraw(tempActualStartPoint, tempStartPoint);
            }
        }
    }

    resizeSinkFreewheel(sinkRaster: any) {
        this.suppressCursors();
        DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'all-scroll');
        this.tool.onMouseMove = (e: any) => {
            SinkItem.resizeSink(sinkRaster, e.delta.x, e.delta.y);
        };
        this.tool.onMouseDrag = null;
        this.tool.onMouseDown = null;
        this.tool.onMouseUp = (e: any) => {
            this.allowCursors();
            this.tool.onMouseDown = (e: any) => this.MouseDownForTool(e);
            this.tool.onMouseMove = (e: any) => this.MouseMoveForTool(e);
            this.tool.onMouseDrag = (e: any) => this.MouseDragForTool(e);
            this.tool.onMouseUp = (e: any) => this.MouseUpForTool(e);
            DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, "default");
            this.MouseUpForTool(e);
            this.updateViewAndAddSavePoint();
        };
    }

    receivedText(strData: any) {
        this.redrawUsingSaveData(JSON.parse(strData));
        this.currentSave = this.drawDataObj.createSaveDrawData();
    }

    checkSplashSelectedUpgradesForValidity(splashData: any) {
        var productTypeID;
        var ec = this.drawDataObj.getEdgeColorFromPath(splashData.segment.path);
        if (ec) {
            productTypeID = ec.ec.p;
            if (splashData.lastProductTypeID != productTypeID) {
                splashData.selectedUpgrades = null;
            }
        } else {
            splashData.selectedUpgrades = null;
        }
    }

    MouseDownForBacksplash() {
        if (SinkItem.hasEnumType(2)) {
            this.menuInfo = this.createMenuSinkDetail(SinkItem.selectedSink, false);
        } else if (CookTopSplashItem.hasEnumType(10)) {
            this.menuInfo = this.createMenuCookTopSplashDetail(CookTopSplashItem.selectedCookTopSplash);
        } else {
            if (this.changeFullRadiusCurve) {
                this.changeCurve = this.changeFullRadiusCurve;
            }
            if (this.changeCurve) {    // A curve was selected
                var curveIndex = this.changeCurve.index;
                if (!this.changeCurve.segment1.data.splashData) {
                    var edgeDataObj = this.changePath.segments[curveIndex].data.edgeData;
                    if (edgeDataObj) {
                        return;
                    }
                    this.menuInfo = this.createMenuBacksplash(this.changeCurve);
                } else {
                    this.menuInfo = null;
                }
            }
        }
    }

    MouseDownForSink() {
        //paper.project.activeLayer.selected = false;
        if (SinkItem.hasNotEnumType(2)) {
            this.menuInfo = this.createMenuSinkDetail(SinkItem.selectedSink, SinkItem.hasNotEnumType(1));
        } else if (this.changePath) {
            this.menuInfo = this.createMenuSink(this.changePath);
        } else {
            this.menuInfo = MenuService.pleaseSelectACounterMenu();
        }
    }

    createBumpLine(curve: any, width: any, depth: any, position: any, bumpType?: any) {
        var path = curve.path;
        if (width > curve.length - 2 * DisplayFunctions.PIXEL_INCH_RATE) {
            width = curve.length - 2 * DisplayFunctions.PIXEL_INCH_RATE;
        }
        if (width <= 0) {
            return;
        }
        //var zeroSide = false;
        if (!bumpType) {
            bumpType = position
            position = (curve.length - width) / 2;
        }
        if (position + width >= curve.length || position <= 0) {
            //zeroSide = true;
            if (position <= 0) {
                position = 0;
            } else {
                position = curve.length - width;
            }
        }

        var bumpVector = this.drawDataObj.createVectorShiftTowardsOutsidePath(path, curve.point1, curve.point2).normalize(depth);
        if (bumpType == 1) {
            bumpVector = MathFunctions.reverseVector(bumpVector);
        }
        var lineVector = MathFunctions.getVectorBetween(curve.point1, curve.point2);
        var hasBacksplash = curve.segment1.data.splashData;
        var hasEdge = curve.segment1.data.edgeData;

        var curve2 = curve.divide(curve.length / 2); //curve.segment1 is OK, curve.segment2 is new.  //curve2.segment2 is OK (pre-existing).
        curve = curve2.previous; //Have to. Weird..
        var curve3 = curve2.divide(curve2.length / 2); //curve2.segment2 is new, curve3.segment2 is OK
        curve2 = curve3.previous;
        var curve4, curve5;
        //if (!zeroSide) {

        curve4 = curve3.divide(curve3.length / 2); //curve3.segment2 is new, curve4.segment2 is OK
        curve3 = curve4.previous;
        curve5 = curve4.divide(curve4.length / 2); //curve4.segment2 is new, curve5.segment2 is OK
        curve4 = curve5.previous;

        curve2.segment1.point = MathFunctions.addVectors(curve.point1, lineVector.normalize(position));
        curve3.segment1.point = MathFunctions.addVectors(curve2.point1, bumpVector);
        curve3.segment2.point = MathFunctions.addVectors(curve3.point1, lineVector.normalize(width));
        curve4.segment2.point = MathFunctions.addVectors(curve3.point2, MathFunctions.reverseVector(bumpVector));

        curve5.segment1.pairedCurve = curve.segment1.pairedCurve;
        curve2.segment1.pairedCurve = curve4.segment1;
        curve4.segment1.pairedCurve = curve2.segment1;

        var bumpGroupData = {
            side1: curve2.segment1, side2: curve4.segment1, tangentSide: curve3.segment1, bumpIn: (bumpType == 1)
        }
        curve2.segment1.data = {
            bumpGroupData: bumpGroupData
        };
        curve3.segment1.data = {
            bumpGroupData: bumpGroupData
        };
        curve4.segment1.data = {
            bumpGroupData: bumpGroupData
        };
        curve5.segment1.data = {};

        curve2.segment1.handleOut = null;
        curve3.segment1.handleIn = null;
        curve3.segment1.handleOut = null;
        curve4.segment1.handleIn = null;
        curve4.segment1.handleOut = null;
        curve5.segment1.handleIn = null;

        if (hasBacksplash) {
            hasBacksplash = {...hasBacksplash};
            hasBacksplash.segment = curve5.segment1;
            this.splash(hasBacksplash);
        }
        if (hasEdge) {
            hasEdge = {...hasEdge};
            hasEdge.segment = curve2.segment1;
            this.edge(hasEdge);
            hasEdge = {...hasEdge};
            hasEdge.segment = curve3.segment1;
            this.edge(hasEdge);
            hasEdge = {...hasEdge};
            hasEdge.segment = curve4.segment1;
            this.edge(hasEdge);
            hasEdge = {...hasEdge};
            hasEdge.segment = curve5.segment1;
            this.edge(hasEdge);
        }
        this.drawDataObj.addToCounterGroup(curve.path, this.createBumpGroup(bumpGroupData));

        this.drawDataObj.redrawInternals(path);
        this.updateViewAndAddSavePoint();
    }

    undo() {
        if (this.undoStack.length === 0)
            return;
        var temp = this.undoStack.pop();
        this.currentSave = temp;
        this.redrawUsingSaveData(temp);
        this.updateEstimate();
    }

    redrawUsingSaveData(data: any) {
        this.drawDataObj.removeDrawData();
        this.drawDataObj.restoreDrawData(data);
        paper.view.update();
    }

    splash(splashData: any) {
        var segment = splashData.segment;
        this.drawDataObj.addSplashToSegment(segment, splashData);
    }

    edge(edgeData: any) {
        var segment = edgeData.segment;
        this.drawDataObj.addEdgeToSegment(segment, edgeData);
    }

    MouseDownForEdgeColor() {

        if (this.changePath) {
            if (this.changeCornerDiagonalCurve || this.changeFullRadiusCurve) {
                this.changeCurve = this.changeCornerDiagonalCurve || this.changeFullRadiusCurve;
            }
            if (this.changeSegment && this.changeSegment.data && (this.changeSegment.data.isSmoothingOnly || this.changeSegment.data.smoothingData)) {
                this.changeSegment = DrawData.getSmoothedStart(this.changeSegment);
                this.changeCurve = this.changeSegment.path.curves[this.changeSegment.index];
            }
            if (this.changeCurve) {    // A curve was selected

                if (!this.changeCurve.segment1.data.splashData) {
                    this.menuInfo = this.createEdgeMenu(this.changePath, this.changeCurve);
                }

            } else if (!this.changeSegment && !this.rightMouseClick) {    // A path was selected ( background was selected )

                var ec = this.drawDataObj.getEdgeColorFromPath(this.changePath);
                var ecData: EdgeColorData;
                if (ec == null) {
                    ecData = DrawData.edgeColorDefault;
                } else {
                    ecData = ec.ec;
                }
                this.hasStep2 = true;
                DrawingCanvasFormSide.dc_showEdgeColorForm(
                    {
                        top: this.canvas.getBoundingClientRect().y + this.currentPoint.y - window.scrollY,
                        left: this.canvas.getBoundingClientRect().x + this.currentPoint.x - window.scrollX
                    },
                    //    {top: currentPoint.y, left: currentPoint.x},
                    ecData,
                    this.adapter
                );
            }
        }

    }

    MouseDownForBump() {
        if (this.changePath) {
            if (this.changeCurve && !this.changeCurve.segment1.data.bumpGroupData) {    // A curve was selected
                this.menuInfo = this.createMenuLineRadius(this.changeCurve);

            } else if (this.changeFullRadiusCurve) {
                this.menuInfo = this.createMenuRemoveLineRadius(this.changeFullRadiusCurve);
            } else if (this.changeCornerDiagonalCurve) {
                this.changeSegment = this.changeCornerDiagonalCurve.segment1;
            }
            if (this.changeSegment && (!this.changeSegment.data.bumpGroupData || !this.changeSegment.data.bumpGroupData.bumpIn) && (!this.changeSegment.previous.data.bumpGroupData || !this.changeSegment.previous.data.bumpGroupData.bumpIn)) {  // A segment was selected
                var prevSideSeg = DrawData.getSmoothedPreviousSegment(this.changeSegment);
                var nextSideSeg = DrawData.getSmoothedEnd(this.changeSegment);
                if (DrawData.isSmoothing(this.changeSegment) || (!DrawData.isRadius(nextSideSeg) && !DrawData.isRadius(prevSideSeg))) {
                    this.changeSegment = DrawData.getSmoothedStart(this.changeSegment);
                    this.menuInfo = this.createMenuCornerRadius(this.changeSegment);
                } else {
                    var seg;
                    if (DrawData.isRadius(DrawData.getSmoothedPreviousSegment(this.changeSegment))) {
                        seg = DrawData.getSmoothedPreviousSegment(this.changeSegment);
                    } else {
                        seg = DrawData.getSmoothedNextSegment(this.changeSegment);
                    }
                    this.changeSegment = null;
                    this.changeFullRadiusCurve = seg.path.curves[seg.index];
                    this.MouseDownForBump();
                }
            }
        }
    }

    changeCurveOnMouseUp(myCurve: any, skipConvert: boolean = false) {
        var curve;
        curve = myCurve.previous;
        if (curve.movingCurveVector) {
            if (curve.length !== 0) {
                curve.movingCurveVector = null;
            }
            curve = myCurve.next;
            if (curve.length !== 0) {
                curve.movingCurveVector = null;
            }
        }
        if (!skipConvert)
            this.drawDataObj.convertCounterToComplexCounter(myCurve.path);
    }

    changeSegmentOnMouseUp(segment: any, skipConvert: boolean = false) {
        var curve2 = segment.path.curves[segment.index],
            curve1 = curve2.previous;
        this.changeCurveOnMouseUp(curve1, true);
        this.changeCurveOnMouseUp(curve2, skipConvert);
    }

    createItemizedCharges() {
        var retArr = this.dataLayerObj.getItemizedCharges();
        this.redrawUsingSaveData(this.currentSave);
        return retArr;
    }

    setPathEdgeColor(path: any, ec: any, doOthers: boolean = false) {
        if (ec) {
            //var pathIndex = getPathIndex(path);

            if (path.pathIndex != null) {
                //var ecLoc = createEdgeColorLocation(path);
                this.drawDataObj.setEdgeColorByPathIndex(path.pathIndex, ec, undefined, doOthers);
                this.VisibleMeasurement();
            }
        }
    }

    createOuterMeasurePointText(ptData: any) {
        var segment = ptData.drawData.curve.segment1;
        return DrawDisplayData.createPointText(
            this.canvas,
            this.adapter,
            ptData,
            () => {
                return (this.measurementVisible.allowAlteringMeasurements);
            },
            null,
            (pt: any) => {
                this.suppressUndoMenu = false;
                this.pointtextfocus = true;
            },
            (value: any) => {
                this.setUserEnteredCurveLength(segment.path.curves[segment.index], value);
                this.drawDataObj.redrawInternals(segment.path);
                this.updateViewAndAddSavePoint();
            });
    }

    createInnerMeasurePointText(ptData: any) {
        return DrawDisplayData.createPointText(
            this.canvas,
            this.adapter,
            ptData,
            () => {
                return (this.measurementVisible.allowAlteringMeasurements);
            },
            null,
            (pt: any) => {
                this.suppressUndoMenu = false;
                this.pointtextfocus = true;
            },
            (value: any) => {
                this.setUserEnteredCurveLength({
                    startSide: ptData.drawData.startSide,
                    endSide: ptData.drawData.endSide
                }, value);
                this.drawDataObj.redrawInternals(ptData.drawData.startSide.path);
                this.updateViewAndAddSavePoint();
            }
        );
    }

    createRadiusMeasurePointText(ptData: any, isCorner: any) {
        var radiusDepth, legendPoint, labelPoint;

        if (isCorner) {
            radiusDepth = ptData.drawData.radiusData.depth;
            legendPoint = ptData.drawData.legendPoint;
            labelPoint = ptData.drawData.labelPoint;
        } else {
            radiusDepth = ptData.drawData.radiusData.trueRadius;
            labelPoint = MathFunctions.addVectors(ptData.point, new Point(-1 * DrawData.MEASUREMENT_LABEL_SHIFT + 12, 0));
            legendPoint = MathFunctions.addVectors(ptData.point, new Point((-2 * DrawData.MEASUREMENT_LABEL_SHIFT) - 12, 0));
        }
        var radLegendText = DrawData.createRadiusLegendText(radiusDepth, isCorner);
        radLegendText = ''; // SAGE-454 Remove the number that shows up behind the full radius indicator
        var style = {...{point: legendPoint, content: radLegendText}, ...DrawDisplayData.RADIUS_LEGEND_STYLE};
        var pt1 = new PointText(style);
        pt1.name = "radiusLegend";
        ptData.point = labelPoint;
        var pt2 = DrawDisplayData.createPointText(this.canvas, this.adapter, ptData,
            () => {
                return (this.measurementVisible.allowAlteringMeasurements);
            },
            (pt: any) => {
                this.adapter.setPointTextValue(DrawDisplayData.extractMeasure(pt.content.slice(1)).toString());
            },
            (pt: any) => {
                this.suppressUndoMenu = false;
                this.pointtextfocus = true;
            },
            (value: any) => {
                ptData.drawData.radiusData.depth = value;
                var path;
                if (ptData.drawData.isCurve) {
                    path = ptData.drawData.curve.path;
                    this.radiusLine(ptData.drawData.curve, ptData.drawData.radiusData.depth, ptData.drawData.radiusData.direction);
                } else {
                    path = ptData.drawData.segment.path;
                    this.radiusOrDiagonal(ptData.drawData.segment, ptData.drawData.radiusData.depth, ptData.drawData.radiusData.type);
                }
                this.drawDataObj.redrawInternals(path);
                this.updateViewAndAddSavePoint();
            },
            DrawDisplayData.RADIUS_LABEL_STYLE
        );
        pt2.name = "radiusLabel";
        return new Group([pt1, pt2]);
    }

    getMatchedRadiusData(curve: any) {
        var pSeg = curve.segment1.pairedCurve;
        if (pSeg && curve.segment1 == pSeg.pairedCurve) {
            var smoothSeg = DrawData.getSmoothedStart(pSeg);
            var simpleCurveLine = DrawData.makeSimpleCounterLine(curve.segment1);
            if (smoothSeg.data.radiusData) {
                var trueRadiusLengthInPixels = MathFunctions.calculateRadiusFromArc(MathFunctions.getLineLength(DrawData.makeSimpleCounterLine(pSeg)), DisplayFunctions.RoundInchToPixel(smoothSeg.data.radiusData.depth));
                var arcDepthInPixels = MathFunctions.calculateArcDepth(MathFunctions.getLineLength(simpleCurveLine), trueRadiusLengthInPixels);
                return {
                    depth: DisplayFunctions.RoundPixelToInch(arcDepthInPixels),
                    direction: 1 * smoothSeg.data.radiusData.direction
                };//was -1.
            }
        }
        return null;
    }

    /** */
    doCursorHitTest(event: paper.ToolEvent) {
        if (this.measurementVisible.suppressCursors
            || this.measurementVisible.alwaysSuppressCursors
            || (ApplicationState.CURRENTMODE > EDIT_MODES.EDGE_COLOR && !(this.drawDataObj.CounterTopArr.length > 0 && this.drawDataObj.CounterTopArr[0].edgecolor && this.drawDataObj.CounterTopArr[0].edgecolor.ec.c != null))) {
            //dc_changeMouseCursor('default');
            if (ApplicationState.CURRENTMODE > EDIT_MODES.EDGE_COLOR && !(this.drawDataObj.CounterTopArr.length > 0 && this.drawDataObj.CounterTopArr[0].edgecolor && this.drawDataObj.CounterTopArr[0].edgecolor.ec.c != null)) {
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
            }
        } else {
            this.highlighedPath.remove();
            var changeObjects = this.getChangeObjects(event.point, DrawData.HITOPTIONS); // Hit Test to recognize which object was selected.
            if (changeObjects.hitResult && changeObjects.hitResult.item && changeObjects.hitResult.item.className === "PointText") {
                this.cursorService.setPointTextCursor(this.measurementVisible.allowAlteringMeasurements, changeObjects.hitResult.item);
            } else if (changeObjects.mySelectedSink && changeObjects.mySelectedSink.data && changeObjects.mySelectedSink.data.typeID && ((this.measurementVisible.sinkCursor && changeObjects.mySelectedSink.data.typeID.typeEnum != 2) || (this.measurementVisible.cutoutCursor && changeObjects.mySelectedSink.data.typeID.typeEnum == 2))) {
                if (changeObjects.mySelectedSink.data.typeID.typeEnum != 2) {
                    this.cursorService.setSinkCursor(this.measurementVisible.sinkCursor, changeObjects.mySelectedSink);
                } else {
                    this.cursorService.setCutoutCursor(this.measurementVisible.cutoutCursor, changeObjects.mySelectedSink);
                }
            } else if (changeObjects.mySelectedCookTopSplash && changeObjects.mySelectedCookTopSplash.data && changeObjects.mySelectedCookTopSplash.data.typeID && ((this.measurementVisible.cookTopSplashCursor && changeObjects.mySelectedCookTopSplash.data.typeID.typeEnum == 10))) {
                this.cursorService.setCookTopSplashCursor(this.measurementVisible.cookTopSplashCursor, changeObjects.mySelectedCookTopSplash);
            } else if (changeObjects.myChangePath) {    // There is any object
                //myChangeSegment, myChangePath, myChangeCurve, myChangeCornerDiagonalCurve, myChangeFullRadiusCurve,
                //mySelectedNonCounterItem, mySelectedSink, myCurrentPoint
                if (changeObjects.myChangeSegment && this.measurementVisible.segmentCursor) {
                    this.cursorService.setSegmentCursor(this.measurementVisible.segmentCursor, changeObjects.myChangeSegment);

                    if (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES) {
                        this.changeSegment = changeObjects.myChangeSegment;
                        this.changePath = changeObjects.myChangePath;

                        if (!this.suppressCanvas) {
                            this.adapter.hideAngle();
                            this.measurementVisible.suppressAltCursors = this.measurementVisible.alwaysSuppressCursors;
                            if (this.hasEdgeColorSelection()) {
                                if (this.changeSegment && (!this.changeSegment.data.bumpGroupData || !this.changeSegment.data.bumpGroupData.bumpIn) && (!this.changeSegment.previous.data.bumpGroupData || !this.changeSegment.previous.data.bumpGroupData.bumpIn)) {  // A segment was selected
                                    if (!this.changeSegment.data.ignoreSegment) {
                                        this.changeSegment = DrawData.getSmoothedStart(this.changeSegment);
                                        this.currentPoint = changeObjects.myCurrentPoint;
                                        this.menuInfo = this.createMenuCornerRadius(this.changeSegment);
                                        this.launchContextMenuMouseMove(event);
                                    }
                                }
                            }
                        }
                    }
                } else if (changeObjects.myChangeCurve && this.measurementVisible.curveCursor) {
                    this.cursorService.setLineCursor(this.measurementVisible.curveCursor, changeObjects.myChangeCurve);
                    this.setHighlightedCurve(changeObjects.myChangeCurve);
                } else if (changeObjects.myChangeFullRadiusCurve) {
                    this.cursorService.setLineCursor(this.measurementVisible.curveCursor, changeObjects.myChangeFullRadiusCurve);
                    this.setHighlightedCurve(changeObjects.myChangeFullRadiusCurve);
                } else if (changeObjects.myChangeCornerDiagonalCurve && ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES) {
                    this.cursorService.setSegmentCursor(this.measurementVisible.segmentCursor);
                    this.setHighlightedCurve(changeObjects.myChangeCornerDiagonalCurve);
                } else if (changeObjects.myChangeCornerDiagonalCurve && (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS || ApplicationState.CURRENTMODE === EDIT_MODES.EDGE_COLOR)) {
                    this.cursorService.setLineCursor(this.measurementVisible.curveCursor, changeObjects.myChangeCornerDiagonalCurve);
                    this.setHighlightedCurve(changeObjects.myChangeCornerDiagonalCurve);
                } else if (this.measurementVisible.pathCursor) {
                    this.cursorService.setPathCursor(this.measurementVisible.pathCursor);
                } else {
                    DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                }
            } else if (changeObjects.mySelectedSink || changeObjects.mySelectedCookTopSplash) {
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
            } else if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS) {
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'pencil-cursor');
            } else {
                DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
            }
        }
    }

    setHighlightedCurve(curve: paper.Curve) {
        this.highlighedPath.removeSegments();
        this.highlighedPath.add(curve.segment1);
        this.highlighedPath.add(curve.segment2);

        var anySegment = <any>curve.segment2;
        var isIgnoredSegment: boolean = false;
        if (anySegment.data.ignoreSegment) {
            isIgnoredSegment = true;
        }

        if (isIgnoredSegment) {
            anySegment = anySegment.next;
            while (anySegment.data && anySegment.data.ignoreSegment) {
                this.highlighedPath.add(anySegment);
                anySegment = anySegment.next;
            }
            this.highlighedPath.add(anySegment);
        }

        paper.project.activeLayer.addChild(this.highlighedPath);
    }

    setUserEnteredCurveLength(curve: any, newLength: any) {
        var path;
        newLength = Math.max(newLength, 0.5);
        if (curve.startSide) {
            //crossMeasure
            path = curve.startSide.path;
            DrawData.convertCounterToSimpleCounter(path);
            this.setUserEnteredCrossMeasureLength(curve, newLength);

        } else {
            path = curve.path;
            DrawData.convertCounterToSimpleCounter(path);
            newLength = DisplayFunctions.RoundInchToPixel(newLength);
            var deltaLength = newLength - curve.length;
            if (!curve.segment1.pairedCurve) {
                var moveVector = MathFunctions.getVectorBetween(curve.point1, curve.point2);
                this.moveSegment(curve.segment2, moveVector.normalize(deltaLength));
                deltaLength = newLength - curve.length;
                if (Math.round(deltaLength) !== 0) {
                    this.moveSegment(curve.segment1, MathFunctions.reverseVector(moveVector).normalize(deltaLength));
                }
                deltaLength = newLength - curve.length;
                if (Math.round(deltaLength) !== 0) {
                    this.moveSegment(curve.segment1, moveVector.normalize(DisplayFunctions.PIXEL_INCH_RATE));
                    this.moveSegment(curve.segment2, MathFunctions.reverseVector(moveVector).normalize(DisplayFunctions.PIXEL_INCH_RATE));
                }
                this.changeSegmentOnMouseUp(curve.segment1, true);
                this.changeSegmentOnMouseUp(curve.segment2, true);
            } else {
                var curve2 = curve.segment1.pairedCurve;
                var curve2Length = curve2.path.curves[curve2.index].length;
                if ((deltaLength < 0) && ((curve2Length + deltaLength) < (DisplayFunctions.PIXEL_INCH_RATE / 2))) { //(curve2.length + deltaLength < 1)) {
                    deltaLength = (DisplayFunctions.PIXEL_INCH_RATE / 2) - curve2Length;
                }
                var deltaIndex = 1;//Math.abs(curve.index - curve2.index);
                var shiftVector = //curve.getNormalAt(curve.length/2).normalize(deltaLength);
                    MathFunctions.getVectorBetween(curve.point1, curve.point2).normalize(deltaLength);

                var nextFunc, seg = curve.segment1, segA = seg.next, segB = seg.previous;
                var moveClockwise = true;
                for (let i = 0; i < curve.path.segments.length; i++) {
                    if (segA.index == curve2.index) {
                        break;
                    }
                    if (segB.index == curve2.index) {
                        moveClockwise = false;
                        break;
                    }
                    segA = segA.next;
                    segB = segB.previous;
                    deltaIndex++;
                }
                if (moveClockwise) {
                    nextFunc = function (s: any) {
                        return s.next;
                    };
                    seg = curve.segment2;
                } else {
                    nextFunc = function (s: any) {
                        return s.previous;
                    };
                    seg = curve.segment1;
                }
                var shiftClockwise = Math.abs(Math.round(MathFunctions.getDistanceBetween(curve.point1, MathFunctions.addVectors(curve.point2, shiftVector))) - newLength) < Math.abs(Math.round(MathFunctions.getDistanceBetween(MathFunctions.addVectors(curve.point1, shiftVector), curve.point2)) - newLength);
                if (shiftClockwise !== moveClockwise) {
                    shiftVector = MathFunctions.reverseVector(shiftVector);
                }
                var prevSeg;
                for (var i = 0; i < deltaIndex; i++) {
                    if (i !== deltaIndex - 1) {
                        seg.point = MathFunctions.addVectors(seg.point, shiftVector);
                        prevSeg = seg;
                        seg = nextFunc(seg);
                    } else {
                        var otherEndSeg = nextFunc(seg);
                        var wouldBePoint = MathFunctions.addVectors(seg.point, shiftVector);
                        var preservedSlopeVector = MathFunctions.getVectorBetween(otherEndSeg.point, seg.point);
                        var intersectLineVector = MathFunctions.getVectorBetween(wouldBePoint, prevSeg.point);
                        var finalPoint = MathFunctions.getInterceptPoint(seg.point, preservedSlopeVector, wouldBePoint, intersectLineVector);
                        seg.point = finalPoint;
                    }
                }//end for
            }//end else
        }//end else

        //checkSidesForConsistency(path);
        this.drawDataObj.convertCounterToComplexCounter(path);
    }

    setUserEnteredCrossMeasureLength(sides: any, newLength: any) {
        newLength = DisplayFunctions.RoundInchToPixel(newLength);
        var startSide = sides.startSide.path.curves[sides.startSide.index];
        var endSide = sides.endSide.path.curves[sides.endSide.index];
        var start = MathFunctions.getMidpoint(startSide.point1, startSide.point2);
        // was var startSideNormalVector = startSide.getNormalAt(MathFunctions.getDistanceBetween(startSide.point1, start));
        var startSideNormalVector = startSide.getNormalAt(0);
        var end = MathFunctions.getInterceptPoint(start, startSideNormalVector, endSide.point1, MathFunctions.getVectorBetween(endSide.point1, endSide.point2));
        var oldLength = MathFunctions.getDistanceBetween(start, end);
        var deltaLength = newLength - oldLength;
        if (deltaLength === 0) {
            return;
        }

        startSideNormalVector = startSideNormalVector.normalize(deltaLength);

        var tryVector1 = MathFunctions.addVectors(MathFunctions.getVectorBetween(start, end), startSideNormalVector);
        var tryVector2 = MathFunctions.addVectors(MathFunctions.getVectorBetween(start, end), MathFunctions.reverseVector(startSideNormalVector));
        if (Math.abs(newLength - tryVector1.length) > Math.abs(newLength - tryVector2.length)) {
            startSideNormalVector = MathFunctions.reverseVector(startSideNormalVector);
        }

        CurveItem.moveCurve(this.drawDataObj, endSide, startSideNormalVector);
        this.changeCurveOnMouseUp(endSide, true);
    }

    hasEdgeColorSelection() {
        if (this.changePath) {

            var ec = this.drawDataObj.getEdgeColorFromPath(this.changePath);
            if (ec) {
                return true;
            }
            return false;
        }
        return true;
    }

    movePath(group: any, delta: any) {
        group.position = MathFunctions.addVectors(group.position, delta);
    }

    bringToFront(p: any) {
        var group = DrawData.getGroupFromPath(p);
        group.bringToFront();

        this.drawDataObj.setSingleCounterMeasurementVisibility(p.pathIndex, this.measurementVisible);
    }

    drawMeasurement(p: any) {
        p.pathIndex = this.drawDataObj.getNextPathIndex();

        for (var i = 0; i < p.curves.length; i++) {
            DrawData.addOuterMeasurementToSegment(p.segments[i], this.drawDataObj.createOuterMeasurement(p.segments[i]));
        }

        var crossMeasurements = this.createCrossMeasuresAndPairSides(p);
        var pathGroup = this.drawDataObj.createNewPathGroup(p);
        this.drawDataObj.addNewCounterData(p, pathGroup);
        this.setPathEdgeColor(p, this.drawDataObj.getDefaultEdgeColor());

        this.VisibleMeasurement();
    }

    createCrossMeasuresAndPairSides(p: any) {
        var n = p.segments.length;
        p.segments[0].isOuterAngle = true;
        for (let i = 1; i < (n / 2) - 1; i++) {//skip endcaps, but Misses curve {n/2, n/2 - 2}
            p.segments[i - 1].pairedCurve = p.segments[n - (i + 1)]; //For resizing in setUserEnteredCurveLength
            p.segments[n - (i + 1)].pairedCurve = p.segments[i - 1]; //For resizing in setUserEnteredCurveLength

            //misses segs: {0,n-1}, {(n/2)-1, (n/2)}
            p.segments[i].pairedAngle = p.segments[n - (i + 1)]; //For rotating during angle changes
            p.segments[n - (i + 1)].pairedAngle = p.segments[i]; //For rotating during angle changes
            p.segments[i].isOuterAngle = (p.curves[i - 1].length > p.curves[n - (i + 1)].length);
            p.segments[n - (i + 1)].isOuterAngle = !(p.segments[i].isOuterAngle);
        }
        p.segments[n / 2].pairedCurve = p.segments[(n / 2) - 2];
        p.segments[(n / 2) - 2].pairedCurve = p.segments[n / 2];

        //pair [n-1] with [1](Dom) or [1].pair(Dom)
        var tempPairedCurve = p.segments[1];
        if (!tempPairedCurve.isOuterAngle && (n != 4)) {
            tempPairedCurve = tempPairedCurve.pairedCurve;
        }
        p.segments[n - 1].pairedCurve = tempPairedCurve;

        //pair [(n/2)-1] with [(n/2)+1](Dom) or ..pair(Dom)
        tempPairedCurve = p.segments[(n / 2) + 1];
        if (!tempPairedCurve.isOuterAngle && (n != 4)) {
            tempPairedCurve = tempPairedCurve.pairedCurve;
        }
        p.segments[(n / 2) - 1].pairedCurve = tempPairedCurve;

        for (let i = 0; i < n; i++) {
            if (p.segments[i].pairedAngle && p.segments[i].isOuterAngle)
                p.segments[i].angleDrawDataObj = this.createAngle(p.segments[i], p.segments[i].pairedAngle);
        }

        // maybe we should use DrawData.convertCounterToSimpleCounter(p) before call
        if (p.curves.length > 6) {
            var lines = (p.curves.length - 6) / 2;
            for (var i = 1; i <= lines; i++) {
                this.drawDataObj.addCrossMeasureAtCurve(p.curves[i].segment1);
            }
        }
    }

    /*
    AddCookTopSplash
    Author: Truyen Nguyen
    */
    makeRasterCookTopSplashBase(position: any, size: any, angle: any, raster: any) {
        var width, height;

        if (!size) {
            width = raster.size.width;
            height = raster.size.height;
            if (isNaN(width) || isNaN(height) || width <= 0 || height <= 0) {
                size = new SimpleSize(DrawDisplayData.NULL_SINK_SIZE.width, DrawDisplayData.NULL_SINK_SIZE.height);
            } else {
                size = new SimpleSize(width, height);
            }
        }

        var pointGroup = new Path.Rectangle(new Point(0, 0), size);
        pointGroup.strokeColor = new paper.Color('black');
        pointGroup.visible = true;
        pointGroup.position = position;
        if (angle)
            pointGroup.rotate(angle);

        raster.name = "CookTopSplashImage";
        pointGroup.name = "CookTopSplashTracker";
        return pointGroup;
    }

    makeClickablePointText(pt: paper.PointText, dataObj: any) {
        if (!dataObj) {
            return;
        }
        pt.onMouseDown = (evt: paper.MouseEvent) => {
            if (!dataObj.doDisplayBoolFunc || dataObj.doDisplayBoolFunc()) {
                var myVal = pt.content;
                if (dataObj.displayFilterFunc) {
                    myVal = dataObj.displayFilterFunc(myVal);
                } else {
                    myVal = DrawDisplayData.extractMeasure(myVal).toString();
                }
                this.adapter.showPointText(
                    pt.bounds.left + this.canvas.offsetLeft - window.scrollX - 2,
                    pt.bounds.top + this.canvas.offsetTop - window.scrollY - 2,
                    Number(myVal),
                    (val) => {
                        if (dataObj.onEnterFunc) {
                            dataObj.onEnterFunc(Number(val));
                            this.drawDataObj.fullRedraw();
                            this.updateViewAndAddSavePoint();
                        }
                    }
                )

                this.suppressUndoMenu = false;
                this.pointtextfocus = true;

                if (dataObj.onDisplayFunc) {
                    dataObj.onDisplayFunc(pt);
                }

                if (dataObj.closingLogicFunc) {
                    dataObj.closingLogicFunc(pt);
                }
            }
        }
    }

    makeClickableGroup(group: any, dataObj: any) {
        if (!dataObj) {
            return;
        }
        if (dataObj.onClickMenuMakerFunc) {
            if (dataObj.mouseOverAllowBoolFunc) {
                group.on('mousemove', (e: any) => {

                    if (this.measurementVisible.suppressAltCursors || this.measurementVisible.alwaysSuppressCursors) {
                        //dc_changeMouseCursor('default');
                    } else if (dataObj.mouseOverAllowBoolFunc()) {
                        this.suppressCursors();
                        DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'pointer');
                    }

                });

                group.on('mouseleave', (e: any) => {
                    if (!this.measurementVisible.suppressAltCursors) {
                        DrawingCanvasFormSide.dc_changeMouseCursor(this.canvas, 'default');
                        this.allowCursors();
                    }
                });
            }
            group.on('mousedown', async (evt: any) => {
                if (dataObj.doDisplayMenuBoolFunc && dataObj.doDisplayMenuBoolFunc(group)) {
                    this.currentPoint = evt.point;
                    this.suppressMousedown = true;
                    this.rightMouseClick = (evt.event.button == 2);
                    this.launchContextMenu(await dataObj.onClickMenuMakerFunc(group));

                    if (dataObj.menuClosingLogicFunc) {
                        dataObj.menuClosingLogicFunc(group);
                    }
                }
            });
        }
        if (dataObj.onDragFunc) {
            dataObj.onDragFunc(group);
        }
        if (dataObj.onMouseUpFunc) {
            dataObj.onMouseUpFunc(group);
        }
    }

    setupSelDepthDlg(segment: any, currentSelDepthType: any, curDepth: any, optionalProducts: IdDescriptionPair[] | undefined, selectedProduct: any = undefined) {
        var depth = curDepth;
        if (!depth) {
            if (currentSelDepthType == 1)
                depth = DrawDisplayData.DEFAULT_CORNER_RADIUS_DEPTH_INCHES;
            else
                depth = DrawDisplayData.DEFAULT_CORNER_DIAGONAL_DEPTH_INCHES;
        }
        this.adapter.setSelDepthHandler((selDepth: number, optionalProduct: number | undefined) => {
            this.radiusOrDiagonal(segment, selDepth, currentSelDepthType, optionalProduct);
            this.updateViewAndAddSavePoint();
            this.drawDataObj.fullRedraw();
        });
        DrawingCanvasFormSide.dc_showSelDepthDlg(this.getModalLocation(), depth, optionalProducts, this.adapter, selectedProduct);
    }

    getChangeSegmentType(segment: any) {
        var myChangeSegment = segment, myChangeFullRadiusCurve;
        var myChangeCurve: paper.Curve | null = null;
        if (segment.data.smoothingData) {
            myChangeSegment = segment.next;
        }
        if (segment.data.ignoreSegment || (ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES && (segment.data.radiusData || segment.previous.data.ignoreSegment))) {
            if (segment.previous.data.ignoreSegment) {
                segment = segment.previous;
            }
            while (segment.data.ignoreSegment) {
                segment = segment.previous;
            }
            if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS) {
                myChangeCurve = segment.path.curves[segment.index];
            } else {
                myChangeFullRadiusCurve = segment.path.curves[segment.index];
            }

            if (ApplicationState.CURRENTMODE !== EDIT_MODES.BUMPS_RADIUSES || myChangeSegment.data.ignoreSegment) {
                myChangeSegment = null;
            }
        }
        return {
            myChangeSegment: myChangeSegment,
            myChangeCurve: myChangeCurve,
            myChangeFullRadiusCurve: myChangeFullRadiusCurve
        };
    }

    getChangeCurveType(myChangeCurve: any) {
        var retVal = {
            myChangeCurve: myChangeCurve,
            myChangeCornerDiagonalCurve: null,
            myChangeFullRadiusCurve: null
        };
        if (myChangeCurve.segment1.data.smoothingData || myChangeCurve.segment1.data.isSmoothingOnly || myChangeCurve.segment1.data.ignoreSegment || myChangeCurve.segment1.data.radiusData || (myChangeCurve.segment1.data.bumpGroupData && ApplicationState.CURRENTMODE === EDIT_MODES.BUMPS_RADIUSES)) {
            if (myChangeCurve.segment1.data.ignoreSegment || myChangeCurve.segment1.data.radiusData) {
                while (myChangeCurve.segment1.data.ignoreSegment) {
                    myChangeCurve = myChangeCurve.previous;
                }
                if (ApplicationState.CURRENTMODE === EDIT_MODES.DRAW_COUNTERS) {
                    retVal.myChangeCurve = myChangeCurve;
                    return retVal;
                }
                retVal.myChangeFullRadiusCurve = myChangeCurve;
            } else if (myChangeCurve.segment1.data.bumpGroupData) {
                retVal.myChangeCurve = myChangeCurve;
                return retVal;
            } else if (myChangeCurve.segment1.data.smoothingData) {
                retVal.myChangeCornerDiagonalCurve = myChangeCurve;

            } else if (myChangeCurve.segment1.data.isSmoothingOnly) {
                //not special
                retVal.myChangeCurve = myChangeCurve;
                return retVal;
            }
            retVal.myChangeCurve = null;
            return retVal;
        }
        return retVal;
    }


    transformWithBuffer(point: any) {
        var _point = point;
        if (_point != null) {
            var eventPointY = _point.y;
            var eventPointX = _point.x;

            if (eventPointY < DrawDisplayData.TOP_BUFFER)
                eventPointY = DrawDisplayData.TOP_BUFFER;
            else if (eventPointY > DrawDisplayData.BOTTOM_BUFFER)
                eventPointY = DrawDisplayData.BOTTOM_BUFFER;

            if (eventPointX > DrawDisplayData.RIGHT_BUFFER)
                eventPointX = DrawDisplayData.RIGHT_BUFFER;
            else if (eventPointX < DrawDisplayData.LEFT_BUFFER)
                eventPointX = DrawDisplayData.LEFT_BUFFER;

            this.currentPoint = new Point(eventPointX, eventPointY);
            return new Point(eventPointX, eventPointY);
        } else {
            return point;
        }
    }

    setupLaminateDlg(productQuoteGroup: any, pathIndex: any, curveIndex: any, isCap: boolean) {
        let items: IdDescriptionPair[] = [];
        var title = "";
        if (isCap) {
            title = "Select Cap";
            items = ApiService.getCapListFromServer(productQuoteGroup);
        } else {
            title = "Select Splash";
            items = ApiService.getSplashListFromServer(productQuoteGroup);
        }

        let value: number | undefined = undefined;
        const segment: any = this.drawDataObj.CounterTopArr[pathIndex].path.segments[curveIndex];
        if (segment.data.edgeLaminateProducts) {
            if (isCap) {
                value = segment.data.edgeLaminateProducts.capProductId;
            } else {
                value = segment.data.edgeLaminateProducts.splashProductId;
            }
        }

        this.adapter.setLaminateHandler((productId: number) => {
            const segment: any = this.drawDataObj.CounterTopArr[pathIndex].path.segments[curveIndex];
            if (!segment.data.edgeLaminateProducts) {
                segment.data.edgeLaminateProducts = {};
            }
            if (isCap) {
                segment.data.edgeLaminateProducts.capProductId = productId;
            } else {
                segment.data.edgeLaminateProducts.splashProductId = productId;
            }
            this.drawDataObj.redrawInternals(this.drawDataObj.CounterTopArr[pathIndex].path);
            this.updateViewAndAddSavePoint();
        });
        DrawingCanvasFormSide.dc_showLaminateDlg(this.getModalLocation(), title, items, this.adapter, value);
    }

    removeLaminate(pathIndex: any, curveIndex: any, isCap: boolean) {
        const segment: any = this.drawDataObj.CounterTopArr[pathIndex].path.segments[curveIndex];
        if (isCap) {
            segment.data.edgeLaminateProducts.capProductId = undefined;
        } else {
            segment.data.edgeLaminateProducts.splashProductId = undefined;
        }
        if (!segment.data.edgeLaminateProducts.capProductId && !segment.data.edgeLaminateProducts.splashProductId) {
            if (segment.data.edgeLaminateProducts.group) {
                segment.data.edgeLaminateProducts.group.remove();
            }
            segment.data.edgeLaminateProducts = undefined;
        }
        this.drawDataObj.redrawInternals(this.drawDataObj.CounterTopArr[pathIndex].path);
        this.updateViewAndAddSavePoint();
    }

    setupBumpDlg(currentBumpType: any, curve: any) {
        this.adapter.setBumpHandler((bumpWidth: number, bumpDepth: number) => {
            this.createBumpLine(
                curve,
                DisplayFunctions.RoundInchToPixel(bumpWidth),
                DisplayFunctions.RoundInchToPixel(bumpDepth),
                currentBumpType);
        });
        DrawingCanvasFormSide.dc_showBumpDlg(this.getModalLocation(), {
            width: DrawingCanvasFormSide.DEFAULTBUMPWIDTH_INCHES,
            depth: DrawingCanvasFormSide.DEFAULTBUMPDEPTH_INCHES
        }, this.adapter);
    }

    resetChangeObjects() {
        this.changeSegment = this.changePath = this.changeCurve = this.changeCornerDiagonalCurve = this.changeFullRadiusCurve = null;
        this.currentPoint = null;
        CookTopSplashItem.setSelectedCookTopSplash(null);
        SinkItem.setSelectedSink(null);
        this.hasHitResult = false;
        if (this.pointtextfocus === false) {
            this.adapter.hidePointText();
        }
        this.pointtextfocus = false;
    }

    smoothSegment(segment: any, smoothLength: any, smoothingData: any, dontAddNewEdges: any) {   //  Function to smooth segment.
        segment = this.diagonalSegment(segment, smoothLength, smoothingData, false, dontAddNewEdges); // at first make a diagonal instead of the segment
        var oldSegPoint = DrawData.getSmoothedPoint(segment);//segment.data.smoothingData.smoothingPointGroup.position;
        /////////////////////// Making the curve to smooth curve ///////////////////////////
        ////////////////////////////////////////////////////////////////////////////////////
        var simpleAngle = DisplayFunctions.customRound(MathFunctions.calculateAngleFromLines(new SimpleLine(segment.point, oldSegPoint), (new SimpleLine(oldSegPoint, segment.next.point))), 2);
        var a = MathFunctions.convertToRadians((180 - simpleAngle) / 2.0);
        var depth = smoothLength * Math.tan(a);
        //var curSDDepth = smoothingData.depth;
        segment = this.diagonalSegment(segment, depth, smoothingData, true, dontAddNewEdges); // at first make a diagonal instead of the segment
        //if(curSDDepth != smoothingData.depth) { //radius was limited by available curveLengths

        //}
        var radiusLineStartPoint = MathFunctions.getMidpoint(segment.point, segment.next.point);
        var vector = MathFunctions.getVectorBetweenPoints(oldSegPoint, radiusLineStartPoint).normalize(DisplayFunctions.RoundInchToPixel(smoothingData.depth) * (((Math.tan(a) * Math.sin(a)) / 2) + 1));

        radiusLineStartPoint = MathFunctions.addVectors(oldSegPoint, vector);
        var radiusLineEndPoint = MathFunctions.addVectors(radiusLineStartPoint, MathFunctions.reverseVector(vector).normalize(DisplayFunctions.RoundInchToPixel(smoothingData.depth)));

        var compromiseVector = MathFunctions.getVectorBetweenPoints(segment.point, oldSegPoint);
        compromiseVector = compromiseVector.normalize(compromiseVector.length * Math.SQRT1_2);

        segment.handleOut = compromiseVector;

        compromiseVector = MathFunctions.getVectorBetweenPoints(segment.next.point, oldSegPoint);
        compromiseVector = compromiseVector.normalize(compromiseVector.length * Math.SQRT1_2);

        segment.next.handleIn = compromiseVector;
        var shift = MathFunctions.getVectorBetweenPoints(radiusLineStartPoint, radiusLineEndPoint).normalize(DrawDisplayData.CORNER_RADIUS_LABEL_SHIFT);
        shift = this.drawDataObj.pointVectorTowardsOutsidePath(segment.path, shift, radiusLineEndPoint);

        var drawData = {
            start: radiusLineStartPoint,
            end: radiusLineEndPoint,
            labelPoint: MathFunctions.addVectors(radiusLineEndPoint, shift),
            legendPoint: radiusLineStartPoint,
            isRadius: true,
            isCorner: true,
            isCurve: false,
            segment: segment,
            radiusData: segment.data.smoothingData
        };
        drawData.radiusData.group = this.drawMeasurementLinesWithArrowsAndText(drawData, null);
        segment.data.smoothingData.group = drawData.radiusData.group;
        this.drawDataObj.addToCounterGroup(drawData.segment.path, drawData.radiusData.group);
        return segment;
    }

    makePointTextForCookTopSplash(path: any, segmentGet: any, currentAngle: any, currentSize: any) {
        var segment = path.segments[segmentGet];
        var cookTopPoint = new Point(segment.point.x, segment.point.y);
        var dataObj = {
            pathShift: DrawDisplayData.SPLASH_BAR_SHIFT,
            pathStyle: DrawDisplayData.SPLASH_STYLE,
            ignoreCornerRadius: false,
            labelText: '' + 4 + ' in. splash',
            labelStyle: DrawDisplayData.SPLASH_LABEL_STYLE,
            labelPosition: 'lateral',
            labelClickOptions: <any>null
        }
        let labelStyle = dataObj.labelStyle ? dataObj.labelStyle : DrawDisplayData.MEASUREMENT_LABEL_STYLE;
        var style = {...{point: cookTopPoint, content: dataObj.labelText, rotation: currentAngle}, ...labelStyle};//field

        //style.position.point = cookTopPoint;

        if (dataObj.labelText.indexOf("in. splash") > -1) {

            style.content = '       ' + '(' + DisplayFunctions.RoundPixelToInch(currentSize.width) + ' x ' + DisplayFunctions.RoundPixelToInch(currentSize.height) + ') in. splash';

            var x1 = path.segments[1].point.x;
            var x0 = path.segments[3].point.x;
            var y1 = path.segments[1].point.y;
            var y0 = path.segments[3].point.y;

            var xs = (x1 + x0) / 2;
            var ys = (y1 + y0) / 2;

            style.point.x = xs;
            style.point.y = ys;
        }

        var pt = new PointText(style);
        this.makeClickablePointText(pt, dataObj.labelClickOptions);//field
        pt.name = 'label';
        return pt;
    }

    positionLoadedRasterCookTopSplash(img: any, size: any, position: any, degrees: any) {
        if (size)
            DrawDisplayData.resizeRaster(img, size);
        if (degrees)
            img.rotate(degrees);
        if (position)
            img.position = position;
    }

    modifyAngle(path: any, segment: any, oldAngle: any, newAngle: any) {
        console.log('Angle.modifyAngle');
        DrawData.convertCounterToSimpleCounter(path);

        var pairedSegment = segment.angleDrawDataObj.pairedSegment;
        var distanceToClockwise = (pairedSegment.index - segment.index);
        if (distanceToClockwise < 0) {
            distanceToClockwise += segment.path.segments.length;
        }
        var distanceToCounterclockwise = (segment.index - pairedSegment.index);
        if (distanceToCounterclockwise < 0) {
            distanceToCounterclockwise += segment.path.segments.length;
        }
        var clockwise: boolean;

        if (distanceToClockwise === distanceToCounterclockwise) {
            clockwise = segment.curve.length <= segment.previous.curve.length;
        } else {
            clockwise = distanceToClockwise < distanceToCounterclockwise;
        }

        var nextSegFunc = (clockwise) ? (s: any) => s.next : (s: any) => s.previous;
        var previousSegFunc = (clockwise) ? (s: any) => s.previous : (s: any) => s.next;
        var rotateSegmentArr = [];
        pairedSegment = nextSegFunc(segment);
        while (pairedSegment != segment.angleDrawDataObj.pairedSegment) {
            rotateSegmentArr.push(pairedSegment);
            pairedSegment = nextSegFunc(pairedSegment);
        }

        var tempSegmentArr = [];
        //Don't move dominant (outer) segment.
        if (clockwise) {
            tempSegmentArr = [segment].concat(rotateSegmentArr);
        } else {
            tempSegmentArr = rotateSegmentArr.concat([pairedSegment]);
        }
        var rotateGroup = DrawData.makeFullGroupWithoutSegments(tempSegmentArr);

        var angleVector1, angleVector2;

        angleVector1 = MathFunctions.reverseVector(MathFunctions.getVectorBetweenPoints(previousSegFunc(segment).point, segment.point));
        //angleVector2 = getVectorBetweenPoints(segment.point, nextSegFunc(segment).point);

        var rotationAngle = newAngle - oldAngle;

        var testPoint = nextSegFunc(segment).point;
        testPoint = testPoint.rotate(rotationAngle, segment.point);
        angleVector2 = MathFunctions.getVectorBetweenPoints(segment.point, testPoint);
        var angleDelta1 = Math.abs(newAngle - angleVector1.getAngle(angleVector2));
        testPoint = nextSegFunc(segment).point;
        testPoint = testPoint.rotate(-1 * rotationAngle, segment.point);
        angleVector2 = MathFunctions.getVectorBetweenPoints(segment.point, testPoint);
        var angleDelta2 = Math.abs(newAngle - angleVector1.getAngle(angleVector2));
        if (angleDelta2 < angleDelta1) {
            rotationAngle *= -1;
        }

        var emergencyUndoVector;
        for (var i = 0; i < rotateSegmentArr.length - 1; i++) {
            rotateSegmentArr[i].point = rotateSegmentArr[i].point.rotate(rotationAngle, segment.point);
        }
        emergencyUndoVector = MathFunctions.getVectorBetweenPoints(pairedSegment.point, rotateSegmentArr[rotateSegmentArr.length - 1].point);
        rotateSegmentArr[rotateSegmentArr.length - 1].point = rotateSegmentArr[rotateSegmentArr.length - 1].point.rotate(rotationAngle, segment.point);
        emergencyUndoVector = emergencyUndoVector.rotate(rotationAngle, <any>undefined);
        //the last one is tricky:
        var interceptLinePoint = MathFunctions.roundVector(nextSegFunc(pairedSegment).point);
        var interceptLineSlopeVector = MathFunctions.roundVector(MathFunctions.getVectorBetweenPoints(pairedSegment.point, interceptLinePoint));
        var startingPoint = MathFunctions.roundVector(previousSegFunc(pairedSegment).point);
        var slopeVector = MathFunctions.roundVector(MathFunctions.getVectorBetweenPoints(segment.point, nextSegFunc(segment).point));
        pairedSegment.point = MathFunctions.getInterceptPoint(startingPoint, slopeVector, interceptLinePoint, interceptLineSlopeVector);
        //check for overlap:
        var d1 = MathFunctions.getDistanceBetween(segment.point, pairedSegment.point);
        var moveSeg = previousSegFunc(pairedSegment);
        var d2 = MathFunctions.getDistanceBetween(segment.point, moveSeg.point);
        if ((newAngle < oldAngle) && (d2 - DisplayFunctions.PIXEL_INCH_RATE) <= d1) {
            var newPoint = MathFunctions.addVectors(pairedSegment.point, emergencyUndoVector.normalize(DisplayFunctions.PIXEL_INCH_RATE));
            var deltaVector = MathFunctions.getVectorBetweenPoints(moveSeg.point, newPoint);

            this.moveSegment(moveSeg, deltaVector, true);
        }

        this.drawDataObj.redrawInternals(path);
        rotateGroup.rotate(rotationAngle, segment.point);
        this.drawDataObj.addToCounterGroup(path, rotateGroup);
        this.drawDataObj.convertCounterToComplexCounter(path);
        //checkSidesForConsistency(path);
        //check sinks:
        this.drawDataObj.rotateHangingSinks(path.pathIndex, rotationAngle, segment.point);

        this.updateViewAndAddSavePoint();
    }

    activateTabs(oldState: EDIT_MODES) {
        this.resetChangeObjects();
        this.adapter.hideAngle();
        switch (ApplicationState.CURRENTMODE) {

            case EDIT_MODES.QUOTE_INFORMATION: {
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = true;
                if (this.isTempPage) {
                    let a = this.drawDataObj.createSaveDrawData();
                    let prodQteGroups = this.dataLayerObj.getProdQteGroups();
                    let c = this.createItemizedCharges();
                    let strData = JSON.stringify(a);
                    this.context.setDrawData(strData);
                    this.context.setImageData(this.getImageData());
                    this.context.setItemizedCharges(c);
                    this.context.setProdQteGroups(prodQteGroups);
                    let materialIds = this.dataLayerObj.getMaterialProducts();
                    this.context.setMaterialProducts(materialIds);
                }
                DrawingCanvasFormSide.dc_switchToQuoteInfo();
                this.drawDataObj.removeDrawData();
                break;
            }
            case EDIT_MODES.DRAW_COUNTERS: {
                this.countertopDrawFunction.edgeInvalidObjs = [];
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = false;
                DrawingCanvasFormSide.dc_switchToCanvas();
                this.VisibleMeasurement(DrawDisplayData.DRAW_COUNTERS_VISIBILITY_TEMPLATE);
                this.redrawUsingSaveData(this.currentSave);
                break;
            }
            case EDIT_MODES.EDGE_COLOR: {
                if (this.countertopDrawFunction.isEdgeInvalid)
                    this.countertopDrawFunction.reSetEdgeInvalidObjs(this.drawDataObj);

                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = false;
                DrawingCanvasFormSide.dc_switchToCanvas();
                this.VisibleMeasurement(DrawDisplayData.EDGE_COLOR_VISIBILITY_TEMPLATE);
                this.redrawUsingSaveData(this.currentSave);
                break;
            }
            case EDIT_MODES.BUMPS_RADIUSES: {
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = false;
                DrawingCanvasFormSide.dc_switchToCanvas();
                this.VisibleMeasurement(DrawDisplayData.BUMPS_RADIUSES_VISIBILITY_TEMPLATE);
                this.redrawUsingSaveData(this.currentSave);
                break;
            }
            case EDIT_MODES.BACKSPLASH: {
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = false;
                DrawingCanvasFormSide.dc_switchToCanvas();
                this.VisibleMeasurement(DrawDisplayData.BACKSPLASH_VISIBILITY_TEMPLATE);
                this.redrawUsingSaveData(this.currentSave);
                this.drawDataObj.bringCookTopSplashsToFront();
                break;
            }
            case EDIT_MODES.SINK_COOKTOP: {
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = false;
                DrawingCanvasFormSide.dc_switchToCanvas();
                this.VisibleMeasurement(DrawDisplayData.SINK_COOKTOP_VISIBILITY_TEMPLATE);
                this.redrawUsingSaveData(this.currentSave);
                this.drawDataObj.bringSinksToFront();
                break;
            }
            case EDIT_MODES.OPTIONAL_ITEMS:
            case EDIT_MODES.PRICING_PRINTING: {
                DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
                this.suppressCanvas = true;
                this.VisibleMeasurement(DrawDisplayData.PRICING_PRINTING_VISIBILITY_TEMPLATE);

                DrawingCanvasFormSide.saveImageToServer(this.canvas);
                var a = this.drawDataObj.createSaveDrawData();
                let prodQteGroups = this.dataLayerObj.getProdQteGroups();
                var c = this.createItemizedCharges();
                let strData = JSON.stringify(a);
                this.context.setDrawData(strData);
                this.context.setImageData(this.getImageData());
                this.context.setItemizedCharges(c);
                this.context.setProdQteGroups(prodQteGroups);
                let materialIds = this.dataLayerObj.getMaterialProducts();
                let materials = this.dataLayerObj.getMaterialSqFtArr();
                this.context.setIs25Check(false);
                let hs25 = new Map<number, number>();

                for (let i = 0; i < materials.length; i++) {
                    let material = materials[i];
                    let currentQty = hs25.get(Number(material.ProductID)) ?? 0 + material.Quantity;
                    hs25.set(Number(material.ProductID), currentQty);
                }

                if (hs25.size === 0) {
                    this.context.setIs25Check(true);
                } else {
                    for (let mid of materialIds) {
                        if ((hs25.get(mid) ?? 0) < 25) {
                            this.context.setIs25Check(true);
                        }
                    }
                }

                this.context.setMaterialProducts(materialIds);
                this.drawDataObj.removeDrawData();
                break;
            }
        }
    }

    getImageData() {
        let cc = this.canvas.getContext("2d");
        if (cc) {
            var data = cc.getImageData(0, 0, this.canvas.width, this.canvas.height);
            let xMin = 0;
            let xMax = 0;
            let yMin = 0;
            let yMax = 0;
            let foundPixel = false;

            // Find xMin
            for (let x = 0; x < data.width; x++) {
                let stop = false;
                for (let y = 0; y < data.height; y++) {
                    let index = (x + y * data.width) * 4 + 3
                    let alpha = data.data[index];
                    if (alpha !== 0) {
                        xMin = x;
                        stop = true;
                        foundPixel = true;
                        break;
                    }
                }
                if (stop)
                    break;
            }

            // Image is empty...
            if (!foundPixel)
                return '';

            // Find yMin
            for (let y = 0; y < data.height; y++) {
                let stop = false;
                for (let x = xMin; x < data.width; x++) {
                    let index = (x + y * data.width) * 4 + 3
                    let alpha = data.data[index];
                    if (alpha !== 0) {
                        yMin = y;
                        stop = true;
                        break;
                    }
                }
                if (stop)
                    break;
            }

            // Find xMax
            for (let x = data.width - 1; x >= xMin; x--) {
                let stop = false;
                for (let y = yMin; y < data.height; y++) {
                    let index = (x + y * data.width) * 4 + 3
                    let alpha = data.data[index];
                    if (alpha !== 0) {
                        xMax = x;
                        stop = true;
                        break;
                    }
                }
                if (stop)
                    break;
            }

            // Find yMax
            for (let y = data.height - 1; y >= yMin; y--) {
                let stop = false;
                for (let x = xMin; x <= xMax; x++) {
                    let index = (x + y * data.width) * 4 + 3
                    let alpha = data.data[index];
                    if (alpha !== 0) {
                        yMax = y;
                        stop = true;
                        break;
                    }
                }
                if (stop)
                    break;
            }

            const margin = 25;
            xMin = xMin - margin;
            if (xMin < 0) {
                xMin = 0;
            }
            yMin = yMin - margin;
            if (yMin < 0) {
                yMin = 0;
            }
            xMax = xMax + margin;
            if (xMax > data.width) {
                xMax = data.width;
            }
            yMax = yMax + margin;
            if (yMax > data.height) {
                yMax = data.height;
            }
            const imageData = cc.getImageData(xMin, yMin, xMax - xMin, yMax - yMin);

            var canvas = document.createElement("canvas");
            canvas.width = imageData.width;
            canvas.height = imageData.height;
            let ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.fillStyle = "white";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.putImageData(imageData, 0, 0);
                return canvas.toDataURL();
            }
        }

        return '';
    }

    changeApplicationMode(mode: EDIT_MODES) {
        let oldState = ApplicationState.CURRENTMODE;
        ApplicationState.CURRENTMODE = mode;

        if (ApplicationState.CURRENTMODE > EDIT_MODES.EDGE_COLOR && this.drawDataObj.CounterTopArr.length > 0 && this.isOutTab === false) {
            this.countertopDrawFunction.reSetEdgeInvalidObjs(this.drawDataObj);
            if (this.countertopDrawFunction.edgeInvalidObjs.length > 0)
                this.countertopDrawFunction.isEdgeInvalid = true;

            if (this.countertopDrawFunction.isEdgeInvalid) {
                this.adapter.showErrorMessage('You did not indicate any finished edges. Please right click and add edging');
                ApplicationState.CURRENTMODE = EDIT_MODES.EDGE_COLOR;
            }
        }

        if (ApplicationState.CURRENTMODE > EDIT_MODES.EDGE_COLOR && !(this.drawDataObj.CounterTopArr.length > 0 && this.drawDataObj.CounterTopArr[0].edgecolor && this.drawDataObj.CounterTopArr[0].edgecolor.ec.c != null)) {
            this.adapter.showErrorMessage('Countertop material is not selected: Please select a material on "Add Material and Edging" tab');
            ApplicationState.CURRENTMODE = EDIT_MODES.EDGE_COLOR;
        }
        this.adapter.setCurrentStep(ApplicationState.CURRENTMODE);

        this.activateTabs(oldState);
    }

    setAdapterHandlers() {
        this.adapter.setAddImageToCacheHandler((val: ProductImage) => {
            ApiService.cache[val.productID] = val;
        });
        this.adapter.setAngleHandler((currentAngleValue: string) => {
            var previousAngleValue = DrawDisplayData.extractMeasure((<any>this.currentAngleSegment).angleDrawDataObj.group.content);
            this.changePath = (<any>this.currentAngleSegment).path;
            (<any>this.currentAngleSegment).angleDrawDataObj.group.content = currentAngleValue;
            this.adapter.hideAngle();
            this.modifyAngle(this.changePath, this.currentAngleSegment, previousAngleValue, currentAngleValue);
            //Change Path
            var moveX, moveY, maxX = 0, maxY = 0;

            var pathGroup = this.drawDataObj.getGroupFromPathIndex(this.changePath.pathIndex);

            var pathAfterRotate = <paper.Path>this.drawDataObj.getPathFromGroup(pathGroup);
            var pathSegments = pathAfterRotate.segments;

            for (var j = 0; j < pathSegments.length; j++) {
                if (pathSegments[j] && pathSegments[j].point) {
                    if ((pathSegments[j].point.x - DrawData.MEASUREMENT_LINE_SHIFT) < 0) {
                        moveX = Math.abs(pathSegments[j].point.x - DrawData.MEASUREMENT_LINE_SHIFT) + 50;
                        if (moveX > maxX) {
                            maxX = moveX;
                        }
                    }
                    if ((pathSegments[j].point.y - DrawData.MEASUREMENT_LINE_SHIFT) < 0) {
                        moveY = Math.abs(pathSegments[j].point.y - DrawData.MEASUREMENT_LINE_SHIFT) + 50;
                        if (moveY > maxY) {
                            maxY = moveY;
                        }
                    }
                }
            }

            if (maxX > 0 || maxY > 0) {
                var pathPosition = new Point(pathGroup.position);
                pathPosition.x += maxX;
                pathPosition.y += maxY;
                pathGroup.position = pathPosition;
                this.updateViewAndAddSavePoint(false);
            }

        });
        this.adapter.setColorHandler((ec: ColorPopupResult) => {
            this.setPathEdgeColor(this.changePath, ec, true);
            this.updateViewAndAddSavePoint();
        });
    } // setAdapterHandlers

    updateEstimate() {
        if (!this.isEstimate)
            return;

        var items = this.createItemizedCharges();
        this.context.setItemizedCharges(items);
    }

    setWindowHanlers() {
        window.onerror = (e) => {
            this.redrawUsingSaveData(this.currentSave);
            return true;
        };

        window.onresize = () => {
            //TODO:
            /*
            Paper.view.viewSize = new Size($(document).width() - this.scrollbarWidth() - 2, DrawDisplayData.CANVASHEIGHT);
            DrawDisplayData.CANVASWIDTH = $(document).width() - this.scrollbarWidth() - 2;
            DrawDisplayData.RIGHT_BUFFER = DrawDisplayData.CANVASWIDTH - DrawDisplayData.DEFAULT_EDGE_BUFFER;
             */
        };
    }

    async preloadData(): Promise<any> {
        DrawDataLoader.initialize();
        this.strDrawindData = this.context.drawData;
        if (this.strDrawindData === '') {
            this.strDrawindData = null;
        }
    }

    ///** Application entry point */
    run(dealerId: number, zip: string) {
        ApiService.initialize(dealerId, zip);

        this.updateView = () => this.updateViewAndAddSavePoint();

        this.setWindowHanlers();
        this.setAdapterHandlers();

        //TODO: check width
        paper.view.viewSize = new Size(window.document.body.clientWidth - this.scrollbarWidth() - 2, DrawDisplayData.CANVASHEIGHT);

        //setup right buffer based on new Canvas Width
        //TODO: check width
        DrawDisplayData.CANVASWIDTH = window.document.body.clientWidth - this.scrollbarWidth() - 2;
        DrawDisplayData.RIGHT_BUFFER = DrawDisplayData.CANVASWIDTH - DrawDisplayData.DEFAULT_EDGE_BUFFER;

        if (this.strDrawindData != null) {
            this.receivedText(this.strDrawindData);
            this.strDrawindData = null;
        }

        if (this.isTempPage) {
            ApplicationState.CURRENTMODE = 2;
            if (this.adapter.currentStep > 2) {
                this.changeApplicationMode(this.adapter.currentStep)
            }
            DrawingCanvasFormSide.changeGuideMode(ApplicationState.CURRENTMODE);
            this.suppressCanvas = false;
            DrawingCanvasFormSide.dc_switchToCanvas();
            this.VisibleMeasurement(DrawDisplayData.DRAW_COUNTERS_VISIBILITY_TEMPLATE);
            this.redrawUsingSaveData(this.currentSave);
        } else {
            this.drawDataObj.fullRedraw();
        }
    }

    scrollbarWidth() {
        //var div = $('<div style="width:50px;height:50px;overflow:hidden;position:absolute;top:-200px;left:-200px;"><div style="height:100px;"></div>');
        var div = document.createElement("div");
        document.body.appendChild(div);
        div.style.width = "50px";
        div.style.visibility = "hidden";
        var widthNoScroll = div.offsetWidth;
        div.style.overflow = "scroll";
        var innerDiv = document.createElement("div");
        innerDiv.style.width = "100%";
        var widthWithScroll = innerDiv.offsetWidth;

        (<any>div.parentNode).removeChild(div);

        return widthNoScroll - widthWithScroll;
    }
}
